<script>
import { nextTick } from 'vue'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

import FolderComponent from './FolderComponent.vue'
import MachinesComponent from './MachinesComponent.vue'
import SamplesComponent from './SamplesComponent.vue'
import AnalyzesComponent from './AnalyzesComponent.vue'
import MachineDetailsComponent from './MachineDetailsComponent.vue'
import AnalyzesLineChartAsync from './charts/AnalyzesLineChartAsync.vue'
import SensorsComponent from './SensorsComponent.vue'
import SensorForm from './SensorFormComponent.vue'
import EventsComponent from './EventsComponent.vue'

import Table from '../common/SortedTable.vue'
import EditForm from '../common/EditForm.vue'
import MessageBoxQuestion from '../common/MessageBoxQuestion.vue'
import MessageBox from '../common/MessageBox.vue'
import SampleEvaluationForm from './SampleEvaluationForm.vue'

import apiUsers from '@/api/api-users.js'
import apiCustomers from '@/api/api-customers.js'
import apiFolders from '@/api/api-folders.js'
import apiMachines from '@/api/api-machines.js'
import apiSamples from '@/api/api-samples.js'
import apiFiles from '@/api/api-files.js'
import apiSettings from '@/api/api-settings'
import apiEvents from '@/api/api-events'
import apiSensors from '@/api/api-sensors.js'
import apiMeasurements from '@/api/api-measurements.js'

import localLanguageMachineTypesCZ from '../../languages/machine-types-cz.json'
import localLanguageMachineTypesEN from '../../languages/machine-types-en.json'
import localLanguageMachineTypesPL from '../../languages/machine-types-pl.json'
import localLanguageOilTypesCZ from '../../languages/oil-types-cz.json'
import localLanguageOilTypesEN from '../../languages/oil-types-en.json'
import localLanguageOilTypesPL from '../../languages/oil-types-pl.json'
import localLanguageOilVTs from '../../languages/oil-vts.json'

export default {
    name: 'MainComponent',

    components: {
        FolderComponent,
        MachinesComponent,
        SamplesComponent,
        AnalyzesComponent,
        MachineDetailsComponent,
        AnalyzesLineChartAsync,
        SensorsComponent,
        EventsComponent,
        Table,
        EditForm,
        MessageBoxQuestion,
        MessageBox,
        SampleEvaluationForm,
        SensorForm
    },

    props: {
        userLanguage: {
            type: String,
            default: 'cz'
        },
        languageStrings: {
            type: Object,
            default: () => {}
        },
        userId: {
            type: Number,
            defaults: -1
        },
        role: {
            type: Number,
            default: 0
        },
        searchString: {
            type: String,
            default: ''
        },
        searchUIN: {
            type: String,
            default: ''
        },
        searchUID: {
            type: String,
            default: ''
        },
        tabFromUrl: {
            type: Number,
            default: 0
        }
    },

    // Data
    data() {
        return {
            localLanguageFiles: { machineTypes: [], oilTypes: [] },
            localLanguageMachineTypesCZ: localLanguageMachineTypesCZ,
            localLanguageMachineTypesEN: localLanguageMachineTypesEN,
            localLanguageMachineTypesPL: localLanguageMachineTypesPL,
            localLanguageOilTypesCZ: localLanguageOilTypesCZ,
            localLanguageOilTypesEN: localLanguageOilTypesEN,
            localLanguageOilTypesPL: localLanguageOilTypesPL,
            localLanguageOilVTs: localLanguageOilVTs,

            enums: {
                regions: [],
                
                deliveryPlaces: []
            },

            lockCustomers: true,
            lockMachines: true,
            lockAnalyzesGraphs: false,

            maxFolders: false,
            maxMachines: false,
            maxTabs: false,

            showTabSubMenu1: false,
            showTabSubMenu2: false,
            showDownloadForm: false,

            showEditForm: false,
            editFormTitle: '',
            editFormData: null,

            messageBoxType: -1,
            showMessageBoxQuestion: false,
            messageBoxQuestionText: '',
            showMessageBox: false,
            messageBoxText: '',
            messageBoxData: null,
            showMessageBoxButtons: true,

            selectedFolder: -10,
            selectedMachines: [],
            selectedSamples: [],
            selectedAnalyze: -1,
            selectedEvent: -1,
            selectedTab: -1,

            machinesOffset: 0,
            machinesWidth: 300,
            machinesWidth2: 600,

            folders: [],
            machines: [],
            samples: [],
            events: [],
            analyzes: [],
            tempAnalyzes: [],
            analyzesFromResults: [],
            graphData: [],

            photoSrc: [],
            attachmentsToDownload: [],

            grafanaLink: '',

            showSampleEvaluation: false,
            sampleForEvaluation: null,

            rerenderSensors: 0,
            showSensorForm: false,
            sensorsList: [],
            selectedSensor: null,
            selectedSensorTemp: null,
            sensorsTabState: 0
        }
    },

    // Computed
    computed: {
        folder() {
            return this.searchTree(this.folders, this.selectedFolder)
        },
        machine() {
            return this.selectedMachines.length > 0 ? this.machines.filter(m => m.id === this.selectedMachines[0]) : []
        },
        diagnosis() {
            return this.selectedAnalyze !== -1 ? this.analyzes.find(a => a.id === this.selectedAnalyze).diagnosis : ''
        },
        actions() {
            return this.selectedAnalyze !== -1 ? this.analyzes.find(a => a.id === this.selectedAnalyze).actions : ''
        },
        sortedEvents() {
            return this.events.slice().sort((a, b) => new Date(b.dateFrom) - new Date(a.dateFrom))
        },

        analyzesDataToDisplay() {
            let arr = []

            if (this.analyzes.length > 0) {
                arr.push([                   
                    { id: 0, value: ' ' },
                    { id: 0, value: ' ' },
                    { id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample001 != null ? this.languageStrings.ui.sample001 : 'Č. vzorku') },
                ])

                if (this.selectedMachines.length != 1 && this.selectedFolder > 0) {
                    arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample003 != null ? this.languageStrings.ui.sample003 : 'Stroj') })
                }

                arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample002 != null ? this.languageStrings.ui.sample002 : 'Datum odběru') })
                arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample007 != null ? this.languageStrings.ui.sample007 : 'Datum příjmu') })
                arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample008 != null ? this.languageStrings.ui.sample008 : 'Datum zkoušky') })
                arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample006 != null ? this.languageStrings.ui.sample006 : 'Popis') })
                arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample004 != null ? this.languageStrings.ui.sample004 : 'Provozní hodiny stroje') })

                if (this.selectedFolder < 0) {
                    arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample015 != null ? this.languageStrings.ui.sample015 : 'Odběrné místo') })
                    arr[0].push({ id: 0, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample003 != null ? this.languageStrings.ui.sample003 : 'Stroj') })
                }

                for (let i = 0; i < this.analyzesFromResults.length; i++) {
                    arr[0].push({ id: 0, value: (Number.isInteger(this.analyzesFromResults[i].group) || this.analyzesFromResults[i].group < 13 || this.analyzesFromResults[i].group >= 15 ? this.getAnalyzeName(this.analyzesFromResults[i].internalKey, this.analyzesFromResults[i].idAls) : '  ' + this.getAnalyzeName(this.analyzesFromResults[i].internalKey, this.analyzesFromResults[i].idAls)) })
                }

                let arrIndex = 1

                let events = this.sortedEvents
                if (this.tempAnalyzes.length > 0) {
                    events = this.sortedEvents.filter(e => e.dateFrom >= this.tempAnalyzes[0].sample.collectionDate)
                }
                for (let k = 0; k < events.length; k++) {
                    arr.push([
                        { id: -1, value: '' },
                        { id: -1, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event009 != null ? this.languageStrings.ui.event009 : 'Událost') },
                        { id: -1, value: '' }
                    ])
                    if (this.selectedMachines.length != 1 && this.selectedFolder > 0) {
                        arr[arrIndex].push({ id: -1, value: (events[k].machine != null ? events[k].machine.name : ' ') })
                    }
                    arr[arrIndex].push({ id: -1, value: this.formatDateTime(events[k].dateFrom) })
                    arr[arrIndex].push({ id: -1, value: '' })
                    arr[arrIndex].push({ id: -1, value: '' })
                    arr[arrIndex].push({ id: -1, value: events[k].category.name, title: events[k].description })
                    arr[arrIndex].push({ id: -1, value: '' })
                    for (let i = 0; i < this.analyzesFromResults.length; i++) {
                        arr[arrIndex].push({ id: -1, value: '' })
                    }
                    arrIndex++
                }

                for (let j = 0; j < this.tempAnalyzes.length; j++) {
                    arr.push([
                        { id: this.tempAnalyzes[j].id, value: ' ' },
                        { id: this.tempAnalyzes[j].id, value: '', attachments: (this.tempAnalyzes[j].attachments != null && this.tempAnalyzes[j].attachments), locked: this.tempAnalyzes[j].sample.locked, analysisConclusion: (this.tempAnalyzes[j].sample.analysisConclusion != null && this.tempAnalyzes[j].sample.analysisConclusion.generalConclusion != null ? this.tempAnalyzes[j].sample.analysisConclusion.generalConclusion : null) },
                        { id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].idAls != null ? this.tempAnalyzes[j].idAls : ' ') }
                    ])
                    if (this.selectedMachines.length != 1 && this.selectedFolder > 0) {
                        arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].sample != null && this.tempAnalyzes[j].sample.machine != null ? this.tempAnalyzes[j].sample.machine.name : ' ') })
                    }
                    arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].sample != null && this.tempAnalyzes[j].sample.collectionDate != null ? this.formatDate(this.tempAnalyzes[j].sample.collectionDate) : this.tempAnalyzes[j].collectionDate != null ? this.formatDate(this.tempAnalyzes[j].collectionDate) : ' ') })
                    arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].receivingDate != null ? this.formatDate(this.tempAnalyzes[j].receivingDate) : ' ') })
                    arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].testDate != null ? this.formatDate(this.tempAnalyzes[j].testDate) : ' ') })
                    arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].sample != null && this.tempAnalyzes[j].sample.description != null ? this.tempAnalyzes[j].sample.description : ' ') })
                    arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].machineWorkingHours != null ? this.tempAnalyzes[j].machineWorkingHours.toString() : ' ') })
                    if (this.selectedFolder < 0) {
                        arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].place != null ? this.tempAnalyzes[j].place : ' '), title: (this.tempAnalyzes[j].place != null ? this.tempAnalyzes[j].place : ' ') })
                        arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (this.tempAnalyzes[j].unit != null  ? this.tempAnalyzes[j].unit : ' ') })
                    }

                    for (let i = 0; i < this.analyzesFromResults.length; i++) {                
                        let r = this.tempAnalyzes[j].results.filter(r => r.analysis != null).find(r => r.analysis.internalKey === this.analyzesFromResults[i].internalKey)

                        if (r != null) {
                            arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: (Number.isNaN(r.value) ? r.value : r.value.replace('.', ',')), semaphore: (r.semaphore != null ? r.semaphore.toLowerCase() : '') })
                        }
                        else {
                            arr[arrIndex].push({ id: this.tempAnalyzes[j].id, value: ' ', semaphore: '' })
                        }
                    }

                    arrIndex++

                    if (j < this.tempAnalyzes.length - 1) {
                        events = this.sortedEvents.filter(e => e.dateFrom <= this.tempAnalyzes[j].sample.collectionDate && e.dateFrom >= this.tempAnalyzes[j + 1].sample.collectionDate)
                    }
                    else {
                        events = this.sortedEvents.filter(e => e.dateFrom <= this.tempAnalyzes[j].sample.collectionDate)
                    }
                    for (let k = 0; k < events.length; k++) {
                        arr.push([
                            { id: -1, value: '' },
                            { id: -1, value: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event009 != null ? this.languageStrings.ui.event009 : 'Událost') },
                            { id: -1, value: '' },
                            { id: -1, value: this.formatDateTime(events[k].dateFrom) },
                            { id: -1, value: '' },
                            { id: -1, value: '' },
                            { id: -1, value: events[k].category.name, title: events[k].description },
                            { id: -1, value: '' },
                        ])
                        for (let i = 0; i < this.analyzesFromResults.length; i++) {
                            arr[arrIndex].push({ id: -1, value: '' })
                        }
                        arrIndex++
                    }
                }
            }

            return arr
        },
        graphOrder() {
            return this.analyzesFromResults.filter(a => a.graph > 0 && a.internalKey != 'AS4059Clas').sort((a, b) => a.graph - b.graph)
        },

        attachmentsToDownloadHeaders() {
            return []
        },
        attachmentsToDownloadRows() {
            let r = []

            for (let i = 0; i < this.attachmentsToDownload.length; i++) {
                r.push({
                    id: this.attachmentsToDownload[i].id,
                    selected: false,
                    cells: [
                        {
                            id: 'name',
                            contenttype: 'text',
                            content: this.attachmentsToDownload[i].name
                        }
                    ]
                })
            }
            return r
        },

        isEditFolderDisabled() {
            return !(this.selectedFolder > 0)
        },
        isAddMachineDisabled() {
            return !(this.selectedFolder > 0)
        },
        isEditMachineDisabled() {
            return !(this.selectedMachines.length === 1)
        },
        isSelectedFolderLinde() {
            if (this.selectedFolder > 0 && this.searchTree(this.folders, this.selectedFolder).name.toLowerCase().startsWith('linde ')) {
                return true
            }
            return false
        }
    },

    // Watch
    watch: {
        languageStrings: function() {
            this.translateExtraFolders()
            if (this.selectedMachines.length !== 0) {
                this.resetEvents()
                this.getEvents()

                this.getGraphsData()
            }
        },

        searchString: async function() {
            if (this.searchString.length > 0) {
                if (isNaN(this.searchString) || this.searchString.length < 8) { // Search folder or machine
                    let f = this.searchTreeByName(this.folders, this.searchString.toLowerCase())
                    if (f != null) { // Folder
                        this.setSelectedFolder(f.id)
                    }
                    else { // Search machine
                        let result = await apiMachines.search(this.searchString)
                        if (result.code === 0) {
                            if (result.data.length > 0) {
                                await this.setSelectedFolder(result.data[0].folder.id)
                                this.setSelectedMachines([ result.data[0].id ])
                            }
                        }
                    }
                }
                else { // SIF
                    let result = await apiSamples.sif(this.searchString)
                    if (result.code === 0) {
                        await this.setSelectedFolder(result.data.machine.folder.id)
                        await this.setSelectedMachines([ result.data.machine.id ])
                        this.selectTab(1)
                        this.selectedSamples = [ result.data.id ]
                    }
                }
            }
        },

        userLanguage: function() {
            if (this.userLanguage === 'pl') {
                this.localLanguageFiles.machineTypes = this.localLanguageMachineTypesPL
                this.localLanguageFiles.oilTypes = this.localLanguageOilTypesPL
            }
            else if (this.userLanguage === 'en') {
                this.localLanguageFiles.machineTypes = this.localLanguageMachineTypesEN
                this.localLanguageFiles.oilTypes = this.localLanguageOilTypesEN
            }
            else {
                this.localLanguageFiles.machineTypes = this.localLanguageMachineTypesCZ
                this.localLanguageFiles.oilTypes = this.localLanguageOilTypesCZ
            }
            this.localLanguageFiles.oilVTs = this.localLanguageOilVTs
        }
    },

    created() {
        if (this.userLanguage === 'pl') {
            this.localLanguageFiles.machineTypes = this.localLanguageMachineTypesPL
            this.localLanguageFiles.oilTypes = this.localLanguageOilTypesPL
        }
        else if (this.userLanguage === 'en') {
            this.localLanguageFiles.machineTypes = this.localLanguageMachineTypesEN
            this.localLanguageFiles.oilTypes = this.localLanguageOilTypesEN
        }
        else {
            this.localLanguageFiles.machineTypes = this.localLanguageMachineTypesCZ
            this.localLanguageFiles.oilTypes = this.localLanguageOilTypesCZ
        }
        this.localLanguageFiles.oilVTs = this.localLanguageOilVTs
    },

    async mounted() {
        if (window.matchMedia("(max-width: 450px)").matches) {
            this.maxFolders = true
        }

        this.getFolders()

        let result = await apiSettings.getRegions()
        if (result.code === 0) {
            this.enums.regions = result.data
        }

        result = await apiSettings.getDeliveryPlaces()
        if (result.code === 0) {
            this.enums.deliveryPlaces = result.data
        }

        if (this.searchUIN.length > 0) {
            let result = await apiMachines.searchUIN(this.searchUIN)
            if (result.code === 0) {
                if (result.data.length > 0) {
                    await this.setSelectedFolder(result.data[0].folder.id)
                    this.setSelectedMachines([ result.data[0].id ])

                    if (this.tabFromUrl > 0) {
                        this.selectTab(this.tabFromUrl)
                    }
                }
            }
        }
        else if (this.searchUID.length > 0) {
            let result = await apiMachines.searchUID(this.searchUID)
            if (result.code === 0) {
                if (result.data.length > 0) {
                    await this.setSelectedFolder(result.data[0].folder.id)
                    this.setSelectedMachines([ result.data[0].id ])

                    if (this.tabFromUrl > 0) {
                        this.selectTab(this.tabFromUrl)
                    }
                }
            }
        }

        // Check url params
        let urlParams = new URLSearchParams(window.location.search);
        const newParams = new URLSearchParams([...urlParams].map(([key, value]) => [key.toLowerCase(), value]));
    
        // Search SIF
        if (newParams.has('sif')) {
            let result = await apiSamples.sif(newParams.get('sif'))
            if (result.code === 0) {
                await this.setSelectedFolder(result.data.machine.folder.id)
                await this.setSelectedMachines([ result.data.machine.id ])
                this.selectTab(1)
                this.selectedSamples = [ result.data.id ]
            }
            history.replaceState(null, null, window.location.origin + window.location.pathname)
        }
    },

    // Methods
    methods: {
        // Helpers
        formatDate(date) {
            return date !== null ? dayjs(date + 'Z').utcOffset(0).format('D.M.YYYY') : ''
        },
        formatDateTime(date) {
            return date !== null ? dayjs(date + 'Z').utcOffset(0).format('D.M.YYYY H:mm') : ''
        },
        arrayBufferToBase64(buffer) {
            var binary = ''
            var bytes = new Uint8Array(buffer)
            var len = bytes.byteLength
            for (var i = 0; i < len; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        },
        stopClickPropagation() {
            // dummy to stop click propagation upwards in hierarchy            
        },
        searchTree(elements, id) {
            if (elements == null || elements.length === 0) {
                return null
            }

            for (let i = 0; i < elements.length; i++) {
                if (elements[i].id === id) {
                    return elements[i]
                }
                else {
                    let result = this.searchTree(elements[i].children, id)
                    if (result != null) {
                        return result
                    }
                }
            }
        },
        getFolderTree(elements, id) {
            if (elements == null || elements.length === 0) {
                return null
            }

            for (let i = 0; i < elements.length; i++) {
                if (elements[i].id === id) {
                    return elements[i]
                }
                else {
                    let result = this.searchTree(elements[i].children, id)
                    if (result != null) {
                        return elements[i]
                    }
                }
            }
        },
        searchTreeByName(elements, name) {
            if (elements == null || elements.length === 0) {
                return null
            }

            for (let i = 0; i < elements.length; i++) {
                if (elements[i].name.toLowerCase().startsWith(name)) {
                    return elements[i]
                }
                else {
                    let result = this.searchTreeByName(elements[i].children, name)
                    if (result != null) {
                        return result
                    }
                }
            }
        },
        isNumeric(str) {
            if (typeof str != "string") return false // we only process strings!  
            return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
                !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
        },
        getFolderIdsInTree(id) {
            let ids = [ id ]

            // get down
            if (this.folder.children?.length > 0) {
                const queue = []
                for (let i = 0; i < this.folder.children.length; i++) {
                    queue.push(this.folder.children[i])
                    while (queue.length > 0) {
                        let child = queue.shift()
                        ids.push(child.id)
                        child.children?.forEach(c => queue.push(c))
                    }
                }
            }

            // get up
            let f = this.folder
            while (f?.parentId != null) {
                f = this.searchTree(this.folders, this.folder.parentId)
                if (f != null) {
                    ids.push(f.id)
                }
            }

            return ids
        },

        // Menu
        triggerTabSubMenuLeave1() {
            setTimeout(() => { this.showTabSubMenu1 = false }, 250)
        },
        triggerTabSubMenuLeave2() {
            setTimeout(() => { this.showTabSubMenu2 = false }, 250)
        },

        // Drag 
        handleDraggingMachines(e) {
            const dx = e.pageX - this.machinesOffset
            this.machinesWidth = dx
        },
        startDraggingMachines(e) {
            this.machinesOffset = e.pageX - this.machinesWidth
            document.addEventListener('mousemove', this.handleDraggingMachines)
        },
        endDragging() {
            document.removeEventListener('mousemove', this.handleDraggingMachines)
        },

        // Locks
        changeLockCustomers() {
            this.lockCustomers = !this.lockCustomers
        },
        changeLockMachines() {
            this.lockMachines = !this.lockMachines
        },
        changeLockAnalyzesGraphs() {
            this.lockAnalyzesGraphs = !this.lockAnalyzesGraphs
        },

        // Maximize
        changeMaxFolders() {
            this.maxFolders = !this.maxFolders
        },
        changeMaxMachines() {
            this.maxMachines = !this.maxMachines
        },
        changeMaxTabs() {
            this.maxTabs = !this.maxTabs
        },

        // Resets
        resetFolders() {
            this.folders = []
            this.selectedFolder = -10
        },
        resetMachines() {
            this.machines = []
            this.selectedMachines = []
        },
        resetSamples() {
            this.attachmentsToDownload = []
            this.samples = []
            this.selectedSamples = []
        },
        resetAnalyzes() {
            this.attachmentsToDownload = []
            this.photoSrc = []
            this.graphData = []
            this.tempAnalyzes = []
            this.analyzes = []
            this.analyzesFromResults = []
            this.selectedAnalyze = -1
        },
        resetEvents() {
            this.events = []
            this.selectedEvent = -1
        },

        // Folders
        async getFolders() {
            this.resetEvents()
            this.resetAnalyzes()
            this.resetSamples()
            this.resetMachines()
            this.resetFolders()

            let result = await apiUsers.getFolders(this.userId)
            if (result.code === 0) {
                this.folders = result.data.filter(f => f.id !== -4)

                this.translateExtraFolders()
            }
        },
        async setSelectedFolder(value) {
            this.resetEvents()
            this.resetAnalyzes()
            this.resetSamples()
            this.resetMachines()

            this.selectedFolder = value

            if (this.selectedFolder !== -10)
                this.selectTab(1)

            if (this.selectedFolder > 0 && this.folder.grafanaUrl != null) {
                this.grafanaLink = this.folder.grafanaUrl
            }
            else {
                this.grafanaLink = ''
            }

            if (this.selectedFolder > 0) {
                await this.getMachines()
                this.getSamples()
            }
            else if (this.selectedFolder === -1 || this.selectedFolder === -2) {
                this.getSamples()
            }
            else if (this.selectedFolder === -3) {
                this.selectTab(2)
                this.getMeasurements()
            }
        },
        async folderDoubleClick(value) {
            this.setSelectedFolder(value)

            this.lockCustomers = false
            this.maxFolders = false
            this.lockMachines = true
            this.maxMachines = true
        },
        async refreshFolders() {
            this.resetEvents()
            this.resetAnalyzes()
            this.resetSamples()
            this.resetMachines()
            this.resetFolders()

            this.selectTab(1)

            this.getFolders()
        },
        async translateExtraFolders() {
            let ef = this.folders.find(f => f.id === -1)
            if (ef != null) {
                if (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree002 != null)
                    ef.name = ef.name.replace(/.+?(?= \()/, this.languageStrings.ui.tree002)
            }

            ef = this.folders.find(f => f.id === -2)
            if (ef != null) {
                if (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree003 != null)
                    ef.name = ef.name.replace(/.+?(?= \()/, this.languageStrings.ui.tree003)
            }

            ef = this.folders.find(f => f.id === -3)
            if (ef != null) {
                if (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree004 != null)
                    ef.name = ef.name.replace(/.+?(?= \()/, this.languageStrings.ui.tree004)
            }

            ef = this.folders.find(f => f.id === -4)
            if (ef != null) {
                if (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree005 != null)
                    ef.name = ef.name.replace(/.+?(?= \()/, this.languageStrings.ui.tree005)
            }
        },
        async getFolderFormData(fId) {
            let customer = null
            if (fId > 0) {
                let result = await apiCustomers.getCustomer(fId)
                if (result.code === 0) {
                    customer = result.data
                }
            }
            else {
                if (this.selectedFolder > 0) {
                    let result = await apiCustomers.getCustomer(this.selectedFolder)
                    if (result.code === 0) {
                        this.folder.region = result.data.region
                    }
                }
            }

            let data = {
                id: fId,
                type: 1,
                rows: [
                    {
                        name: 'name',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree012 != null ? this.languageStrings.ui.tree012 : 'Název'),
                        value: customer != null && customer.name != null ? customer.name : '',
                        required: true
                    },
                    {
                        name: 'region',
                        type: 'options',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree013 != null ? this.languageStrings.ui.tree013 : 'Region'),
                        value: customer != null && customer.region != null ? customer.region.id : this.selectedFolder > 0 && this.folder.region != null ? this.folder.region.id : 0,
                        options: [],
                        required: true
                    },
                    {
                        name: 'street',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree014 != null ? this.languageStrings.ui.tree014 : 'Ulice'),
                        value: customer != null && customer.street != null ? customer.street : '',
                        required: false
                    },
                    {
                        name: 'city',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree015 != null ? this.languageStrings.ui.tree015 : 'Město'),
                        value: customer != null && customer.city != null ? customer.city : '',
                        required: false
                    },
                    {
                        name: 'zip',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree016 != null ? this.languageStrings.ui.tree016 : 'PSČ'),
                        value: customer != null && customer.zipCode != null ? customer.zipCode : '',
                        required: false
                    },
                    {
                        name: 'country',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree017 != null ? this.languageStrings.ui.tree017 : 'Země'),
                        value: customer != null && customer.country != null ? customer.country : '',
                        required: false
                    },
                    {
                        name: 'first-name',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree018 != null ? this.languageStrings.ui.tree018 : 'OO - Jméno'),
                        value: customer != null && customer.firstName != null ? customer.firstName : '',
                        required: false
                    },
                    {
                        name: 'last-name',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree019 != null ? this.languageStrings.ui.tree019 : 'OO - Příjmení'),
                        value: customer != null && customer.lastName != null ? customer.lastName : '',
                        required: false
                    },
                    {
                        name: 'phone',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree020 != null ? this.languageStrings.ui.tree020 : 'OO - Telefon'),
                        value: customer != null && customer.phoneNumber != null ? customer.phoneNumber : '',
                        required: false
                    },
                    {
                        name: 'email',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree021 != null ? this.languageStrings.ui.tree021 : 'OO - E-mail'),
                        value: customer != null && customer.email != null ? customer.email : '',
                        required: false
                    },
                    {
                        name: 'comment',
                        type: 'text-multiline',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree022 != null ? this.languageStrings.ui.tree022 : 'Poznámka'),
                        value: customer != null && customer.description != null ? customer.description : '',
                        required: false
                    },
                    {
                        name: 'kleentek',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree023 != null ? this.languageStrings.ui.tree023 : 'OO - Kleentek'),
                        value: customer != null && customer.kleentek != null ? customer.kleentek : '',
                        required: false
                    },
                    {
                        name: 'private',
                        type: 'checkbox',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree026 != null ? this.languageStrings.ui.tree026 : 'Vyřazené',
                        value: customer != null && customer.type != null && customer.type.id == 7 ? true : false,
                        required: false
                    }
                ]
            }

            if (this.isSelectedFolderLinde) {
                data.rows.push({
                    name: 'tag-linde',
                    type: 'text',
                    text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree024 != null ? this.languageStrings.ui.tree024 : 'Tag LINDE'),
                    value: customer != null && customer.tagLinde != null ? customer.tagLinde : '',
                    required: false
                })
            }

            if (this.role === 4) {
                data.rows.push(...[
                    {
                        name: 'grafana-url',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree025 != null ? this.languageStrings.ui.tree025 : 'Grafana'),
                        value: customer != null && customer.grafanaUrl != null ? customer.grafanaUrl : '',
                        required: false
                    },
                    {
                        name: 'internal-key',
                        type: 'text',
                        text: 'InternalKey',
                        value: customer != null && customer.internalKey != null ? customer.internalKey : '',
                        required: false
                    }
                ])
            }

            data.rows[1].options.push({ key: 0, value: '' })
            data.rows[1].options.push(...this.enums.regions.slice().sort((a, b) => a.name.localeCompare(b.name)).map(r => ( { key: r.id, value: r.name } )))

            return data
        },
        async addFolder() {
            if (this.selectedFolder <= 0) {
                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree006 != null ? this.languageStrings.ui.tree006 : 'Nový zákazník'
            }
            else {
                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree008 != null ? this.languageStrings.ui.tree008 : 'Nová složka'
            }
            this.editFormData = await this.getFolderFormData(-1)
            this.showEditForm = true
        },
        async editFolder() {
            if (this.isEditFolderDisabled) {
                return
            }
            
            if (this.folder.parentId == null) {
                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree007 != null ? this.languageStrings.ui.tree007 : 'Editace zákazníka'
            }
            else {
                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree009 != null ? this.languageStrings.ui.tree009 : 'Editace složky'
            }
            this.editFormData = await this.getFolderFormData(this.selectedFolder)
            this.showEditForm = true
        },
        async deleteFolder() {
            if (this.isEditFolderDisabled) {
                return
            }

            if (this.folder.parentId != null) { // folder
                this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree011 != null ? this.languageStrings.ui.tree011 : 'Smazat složku a vše co obsahuje?'
            }
            else { // customer
                this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tree010 != null ? this.languageStrings.ui.tree010 : 'Smazat zákazníka a vše co obsahuje?'
            }
            this.messageBoxType = 1
            this.showMessageBoxQuestion = true
        },
        async deleteFolderRecursive(f) {
            if (f.children != null) {
                for (let i = 0; i < f.children.length; i++) {
                    let r = await this.deleteFolderRecursive(f.children[i])
                    if (r === -1) {
                        return -1
                    }
                }
            }
            let res = await apiCustomers.deleteCustomer(f.id)
            if (res.code !== 0) {
                return -1
            }
            return 0
        },

        // Machines
        async getMachines() {
            if (this.selectedFolder > 0) {
                let sf = this.selectedFolder
                let result = await apiCustomers.getMachines(sf)
                if (result.code === 0 && sf === this.selectedFolder) {
                    this.machines = result.data
                }

                if (this.folder.grafanaUrl != null) {
                    this.grafanaLink = this.folder.grafanaUrl
                }
                else {
                    this.grafanaLink = ''
                }
            }
        },
        async setSelectedMachines(value) {
            this.resetEvents()
            this.resetAnalyzes()
            this.resetSamples()

            if (this.selectedMachines.length === 0 && this.selectedTab !== 3)
                this.selectTab(2)

            this.selectedMachines = value

            if (this.selectedMachines.length > 0 && this.machine[0].grafanaUrl != null && this.machine[0].grafanaUrl.length > 0) {
                this.grafanaLink = this.machine[0].grafanaUrl
            }
            else {
                if (this.selectedFolder > 0 && this.folder.grafanaUrl != null) {
                    this.grafanaLink = this.folder.grafanaUrl
                }
                else {
                    this.grafanaLink = ''
                }
            }

            this.getEvents()
            await this.getSamples()
            await this.getMeasurements()
        },
        async machineDoubleClick() {
            this.maxMachines = false
            this.lockMachines = false
        },
        async refreshMachines() {
            this.resetEvents()
            this.resetAnalyzes()
            this.resetSamples()
            this.resetMachines()

            this.selectTab(1)

            await this.getMachines()
            this.getSamples()
        },
        getMachineFormData(mId) {
            let machine = this.machines.find(m => m.id === mId)
            let data = {
                id: mId,
                type: 2,
                rows: [
                    {
                        name: 'name',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine001 != null ? this.languageStrings.ui.machine001 : 'Název'),
                        value: machine != null && machine.name != null ? machine.name : '',
                        required: true
                    },
                    {
                        name: 'customer-id',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine003 != null ? this.languageStrings.ui.machine003 : 'ID stroje'),
                        value: machine != null && machine.customerId != null ? machine.customerId : '',
                        required: false
                    },
                    {
                        name: 'producer',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine004 != null ? this.languageStrings.ui.machine004 : 'Výrobce'),
                        value: machine != null && machine.producer != null ? machine.producer : '',
                        required: false
                    },
                    {
                        name: 'machine-type',
                        type: 'options',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine005 != null ? this.languageStrings.ui.machine005 : 'Typ stroje'),
                        value: machine != null && machine.type != null && machine.type.length > 0 ? machine.type : '',
                        options: [],
                        required: false
                    },
                    {
                        name: 'model',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine006 != null ? this.languageStrings.ui.machine006 : 'Model'),
                        value: machine != null && machine.model != null ? machine.model : '',
                        required: false
                    },
                    {
                        name: 'year-produced',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine007 != null ? this.languageStrings.ui.machine007 : 'Rok výroby'),
                        value: machine != null && machine.yearProduced != null ? machine.yearProduced : '',
                        required: false
                    },
                    {
                        name: 'volume',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine008 != null ? this.languageStrings.ui.machine008 : 'Objem oleje (l / kg)'),
                        value: machine != null && machine.volume != null ? machine.volume : '',
                        required: false
                    },
                    {
                        name: 'oil-producer',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine009 != null ? this.languageStrings.ui.machine009 : 'Výrobce oleje'),
                        value: machine != null && machine.oilProducer != null ? machine.oilProducer : '',
                        required: false
                    },
                    {
                        name: 'oil-brand',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine010 != null ? this.languageStrings.ui.machine010 : 'Značka oleje'),
                        value: machine != null && machine.oilBrand != null ? machine.oilBrand : '',
                        required: true
                    },
                    {
                        name: 'oil-type',
                        type: 'options',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine011 != null ? this.languageStrings.ui.machine011 : 'Typ oleje'),
                        value: machine != null && machine.oilType != null ? machine.oilType.id : 0,
                        options: [],
                        required: false
                    },
                    {
                        name: 'oil-vt',
                        type: 'options',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine012 != null ? this.languageStrings.ui.machine012 : 'VT oleje'),
                        value: machine != null && machine.machineOil != null ? machine.machineOil.id : 0,
                        options: [],
                        required: false
                    },
                    {
                        name: 'medium',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine014 != null ? this.languageStrings.ui.machine014 : 'Médium'),
                        value: machine != null && machine.medium != null ? machine.medium : '',
                        required: false
                    },
                    {
                        name: 'description',
                        type: 'text-multiline',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine015 != null ? this.languageStrings.ui.machine015 : 'Poznámka'),
                        value: machine != null && machine.description != null ? machine.description : '',
                        required: false
                    }
                ]
            }

            if (this.role > 1 && this.role < 5) {
                data.rows.push({
                    name: 'uin-als',
                    type: 'text',
                    text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine018 != null ? this.languageStrings.ui.machine018 : 'UIN'),
                    value: machine != null && machine.uinals != null ? machine.uinals : '',
                    required: false
                })

                if (this.isSelectedFolderLinde) {
                    data.rows.push({
                        name: 'tag-linde',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine019 != null ? this.languageStrings.ui.machine019 : 'Tag LINDE'),
                        value: machine != null && machine.tagLinde != null ? machine.tagLinde : '',
                        required: false
                    })
                }

                if (this.role === 4) {
                    data.rows.push(...[
                        {
                            name: 'grafana-url',
                            type: 'text',
                            text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine022 != null ? this.languageStrings.ui.machine022 : 'Grafana'),
                            value: machine != null && machine.grafanaUrl != null ? machine.grafanaUrl : '',
                            required: false
                        },
                        {
                            name: 'internal-key',
                            type: 'text',
                            text: 'InternalKey',
                            value: machine != null && machine.internalKey != null ? machine.internalKey : '',
                            required: false
                        }
                    ])
                }
            }

            data.rows[3].options.push({ key: '', value: '' })
            data.rows[3].options.push(...this.localLanguageFiles.machineTypes.map(mt => ( { key: mt.id, value: mt.name } )))

            data.rows[9].options.push({ key: 0, value: '' })
            data.rows[9].options.push(...this.localLanguageFiles.oilTypes.map(ot => ( { key: ot.id, value: ot.name } )))

            data.rows[10].options.push({ key: 0, value: '' })
            data.rows[10].options.push(...this.localLanguageFiles.oilVTs.map(ov => ( { key: ov.id, value: ov.name } )))

            return data
        },
        async addMachine() {
            if (this.isAddMachineDisabled) {
                return
            }

            this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine016 != null ? this.languageStrings.ui.machine016 : 'Nový stroj'
            this.editFormData = this.getMachineFormData(-1)
            this.showEditForm = true
        },
        async editMachine() {
            if (this.isEditMachineDisabled) {
                return
            }

            this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine017 != null ? this.languageStrings.ui.machine017 : 'Editace stroje'
            this.editFormData = this.getMachineFormData(this.selectedMachines[0])
            this.showEditForm = true
        },
        async moveMachine() {
            if (this.isEditMachineDisabled) {
                return
            }

            this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine023 != null ? this.languageStrings.ui.machine023 : 'Přesun stroje'
            
            let machine = this.machines.find(m => m.id === this.selectedMachines[0])

            let f = this.getFolderTree(this.folders, machine.folder.id)
            const flatFolders = []
            const queue = []
            queue.push(f)
            while (queue.length > 0) {
                let child = queue.shift()
                flatFolders.push(child)
                child.children?.forEach(c => queue.push(c))
            }

            let data = {
                id: machine.id,
                type: 5,
                rows: [
                    {
                        name: 'folder',
                        type: 'options',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine002 != null ? this.languageStrings.ui.machine002 : 'Složka'),
                        value: machine.folder.id,
                        options: [],
                        required: true
                    }
                ]
            }
            data.rows[0].options.push(...flatFolders.map(f => ( { key: f.id, value: f.name } )))
            this.editFormData = data
            this.showEditForm = true
        },
        async deleteMachine() {
            if (this.isEditMachineDisabled) {
                return
            }

            this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.machine021 != null ? this.languageStrings.ui.machine021 : 'Smazat stroj?'
            this.messageBoxType = 2
            this.showMessageBoxQuestion = true
        },

        // Tabs
        selectTab(tab) {
            this.showTabLi = false
            this.selectedTab = tab
        },
        async refreshTab() {
            switch (this.selectedTab) {
                case 1:
                    this.getSamples()
                    break

                case 2:
                    // this.resetAnalyzes()

                    // if (this.selectedMachines.length > 0)
                    await this.getMeasurements()
                    break

                case 3:
                    this.selectedSensor = null
                    this.rerenderSensors = this.rerenderSensors + 1
                    break
                    
                case 4:
                    this.resetEvents()

                    this.getEvents()
                    break
            }
        },

        // Samples
        async getSamples() {
            let sf = this.selectedFolder
            let sm = this.selectedMachines

            this.resetSamples()

            if (this.selectedFolder > 0) {
                if (this.selectedMachines.length === 0) {
                    let result = await apiFolders.getSamples(this.selectedFolder)
                    if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                        this.samples = result.data

                        this.getSamplesAttachments()
                    }
                }
                else {
                    let s = []
                    let resultCode = 0
                    for (let i = 0; i < this.selectedMachines.length; i++) {
                        let result = await apiMachines.getSamples(this.selectedMachines[i])
                        if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                            s.push(...result.data)
                        }
                        else {
                            resultCode = result.code
                        }
                    }                    
                    if (resultCode === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                        this.samples = s.sort((a, b) => b.collectionDate > a.collectionDate ? 1 : -1)
                        this.getSamplesAttachments()
                    }
                }
            }
            else if (this.selectedFolder === -1 || this.selectedFolder === -2) {
                let result = await apiMachines.getSamples(this.selectedFolder)
                if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                    this.samples = result.data
                }
            }
        },
        setSelectedSamples(value) {
            this.selectedSamples = value

            if (this.selectedMachines.length == 0) {
                this.getMeasurements()
            }
        },
        async sampleDoubleClick() {
            if (this.selectedFolder > 0) {
                this.setSelectedMachines([ this.samples.find(s => s.id === this.selectedSamples[0]).machine.id ])
            }
            else if (this.selectedFolder === -1 || this.selectedFolder === -2) {
                let s = this.samples.find(s => s.id === this.selectedSamples[0])
                await this.setSelectedFolder(s.machine.folder.id)
                this.setSelectedMachines([ s.machine.id ])
                this.selectTab(1)
                this.setSelectedSamples([ s.id ])
            }
        },
        async getSamplesAttachments() {
            let sf = this.selectedFolder
            let sm = this.selectedMachines

            for (let i = 0; i < this.samples.length; i++) {
                let result = await apiSamples.getSamplesAttachments(this.samples[i].id)

                // Check if user did not changed array or clicked away
                if (i >= this.samples.length || sf !== this.selectedFolder || sm.length !== this.selectedMachines.length) {
                    break
                }

                if (result.code === 0) {
                    // Attachments starting with _ are not visible to customers
                    this.samples[i].attachments = this.role != 1 ? result.data : result.data.filter(a => !a.name.startsWith('_'))
                }
                else {
                    this.samples[i].attachments = []
                }
            }
        },
        async getSampleAttachments(id) {
            let sf = this.selectedFolder
            let sm = this.selectedMachines

            let s = this.samples.filter(s => s.id === id)[0]
            s.attachments = []

            let result = await apiSamples.getSamplesAttachments(id)

            // Check if user did not changed array or clicked away
            if (sf !== this.selectedFolder || sm.length !== this.selectedMachines.length) {
                return
            }

            if (result.code === 0) {
                // Attachments starting with _ are not visible to customers
                s.attachments = this.role != 1 ? result.data : result.data.filter(a => !a.name.startsWith('_'))
            }
        },
        async showSampleAttachments() {
            await this.showAttachments()
        },
        async getSampleFormData(sId) {
            let sample = this.samples.find(s => s.id === sId)
            let data = {
                id: sId,
                type: 3,
                rows: [
                    {
                        name: 'sample-number',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample001 != null ? this.languageStrings.ui.sample001 : 'Č. vzorku'),
                        value: sample != null && sample.sif != null ? sample.sif : '',
                        required: true
                    },
                    {
                        name: 'als-number',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample009 != null ? this.languageStrings.ui.sample009 : 'Č. vzorku ALS'),
                        value: sample != null && sample.alsNumber != null ? sample.alsNumber : '',
                        required: true
                    },
                    {
                        name: 'collection-date',
                        type: 'datetime',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample002 != null ? this.languageStrings.ui.sample002 : 'Datum odběru'),
                        value: sample != null && sample.collectionDate != null ? dayjs(sample.collectionDate + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:00') : null,
                        required: false
                    },
                    {
                        name: 'delivery-place',
                        type: 'options',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample010 != null ? this.languageStrings.ui.sample010 : 'Místo předání'),
                        value: sample != null && sample.deliveryPlace != null ? sample.deliveryPlace.id : 0,
                        options: [],
                        required: false
                    },
                    {
                        name: 'receiving-date',
                        type: 'date',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample007 != null ? this.languageStrings.ui.sample007 : 'Datum příjmu'),
                        value: sample != null && sample.receivingDate != null ? dayjs(sample.receivingDate + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:00') : null,
                        required: false
                    },
                    {
                        name: 'processing-date',
                        type: 'date',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample008 != null ? this.languageStrings.ui.sample008 : 'Datum zkoušky'),
                        value: sample != null && sample.processingDate != null ? dayjs(sample.processingDate + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:00') : null,
                        required: false
                    },
                    {
                        name: 'exchange-date',
                        type: 'date',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample011 != null ? this.languageStrings.ui.sample011 : 'Datum výměny'),
                        value: sample != null && sample.exchangeDate != null ? dayjs(sample.exchangeDate + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:00') : null,
                        required: false
                    },
                    {
                        name: 'exchanged',
                        type: 'checkbox',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample012 != null ? this.languageStrings.ui.sample012 : 'Vyměněno'),
                        value: sample != null && sample.exchanged != null ? sample.exchanged : false,
                        required: false
                    },
                    {
                        name: 'supplemented',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample013 != null ? this.languageStrings.ui.sample013 + ' [l]' : 'Doplněno [l]'),
                        value: sample != null && sample.supplemented != null ? sample.supplemented : '',
                        required: false
                    },
                    {
                        name: 'machine-operating-time',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample004 != null ? this.languageStrings.ui.sample004 + ' [h]' : 'Provozní hodiny stroje [h]'),
                        value: sample != null && sample.machineWorkingHours != null ? sample.machineWorkingHours : '',
                        required: false
                    },
                    {
                        name: 'oil-operating-time',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample014 != null ? this.languageStrings.ui.sample014 + ' [h]' : 'Provozní hodiny oleje [h]'),
                        value: sample != null && sample.oilWorkingHours != null ? sample.oilWorkingHours : '',
                        required: false
                    },
                    {
                        name: 'collection-place',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample015 != null ? this.languageStrings.ui.sample015 : 'Odběrné místo'),
                        value: sample != null && sample.collectionPlace != null ? sample.collectionPlace : '',
                        required: false
                    },
                    {
                        name: 'collected',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample016 != null ? this.languageStrings.ui.sample016 : 'Odběr provedl'),
                        value: sample != null && sample.collectionPersonName != null ? sample.collectionPersonName : '',
                        required: false
                    },
                    {
                        name: 'oil-temperature',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample017 != null ? this.languageStrings.ui.sample017 + ' [°C]' : 'Teplota oleje [°C]'),
                        value: sample != null && sample.oilTemperature != null ? sample.oilTemperature : '',
                        required: false
                    },
                    {
                        name: 'ambient-temperature',
                        type: 'text',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample018 != null ? this.languageStrings.ui.sample018 + ' [°C]' : 'Teplota prostředí [°C]'),
                        value: sample != null && sample.environmentTemperature != null ? sample.environmentTemperature : '',
                        required: false
                    },
                    {
                        name: 'description',
                        type: 'text-multiline',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample006 != null ? this.languageStrings.ui.sample006 : 'Popis'),
                        value: sample != null && sample.description != null ? sample.description : '',
                        required: false
                    }
                ]
            }

            // Delivery places
            data.rows[3].options.push({ key: 0, value: '' })
            data.rows[3].options.push(...this.enums.deliveryPlaces.map(r => ( { key: r.id, value: r.name } )))

            return data
        },
        async addSample() {
            this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample028 != null ? this.languageStrings.ui.sample028 : 'Nový vzorek'
            this.editFormData = await this.getSampleFormData(-1)
            this.showEditForm = true
        },
        async editSample() {
            if (this.selectedTab === 1 && this.selectedSamples.length > 0) { // Samples
                if (this.samples.filter(s => s.id === this.selectedSamples[0])[0].locked) {
                    this.messageBoxText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample032 != null ? this.languageStrings.ui.sample032 : 'Vzorek je uzamčen.'
                    this.showMessageBox = true
                    return
                }

                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample029 != null ? this.languageStrings.ui.sample029 : 'Editace vzorku'
                this.editFormData = await this.getSampleFormData(this.selectedSamples[0])
                this.showEditForm = true
            }
            else if (this.selectedTab === 2 && this.selectedAnalyze !== -1) { //Analyzes
                let s = this.analyzes.filter(a => a.id === this.selectedAnalyze)[0].sample
                if (s.locked) {
                    this.messageBoxText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample032 != null ? this.languageStrings.ui.sample032 : 'Vzorek je uzamčen.'
                    this.showMessageBox = true
                    return
                }

                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample029 != null ? this.languageStrings.ui.sample029 : 'Editace vzorku'
                this.editFormData = await this.getSampleFormData(s.id)
                this.showEditForm = true
            }
        },
        async deleteSample() {
            if (this.selectedTab === 1 && this.selectedSamples.length > 0) { // Samples
                if (this.samples.filter(s => s.id === this.selectedSamples[0])[0].locked) {
                    this.messageBoxText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample032 != null ? this.languageStrings.ui.sample032 : 'Vzorek je uzamčen.'
                    this.showMessageBox = true
                    return
                }

                this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample031 != null ? this.languageStrings.ui.sample031 : 'Smazat vzorek?'
                this.messageBoxType = 3
                this.showMessageBoxQuestion = true
            }
            else if (this.selectedTab === 2 && this.selectedAnalyze !== -1) { //Analyzes
                let s = this.analyzes.filter(a => a.id === this.selectedAnalyze)[0].sample
                if (s.locked) {
                    this.messageBoxText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample032 != null ? this.languageStrings.ui.sample032 : 'Vzorek je uzamčen.'
                    this.showMessageBox = true
                    return
                }

                this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample031 != null ? this.languageStrings.ui.sample031 : 'Smazat vzorek?'
                this.messageBoxType = 3
                this.showMessageBoxQuestion = true
            }
        },
        async evaluateSample() {
            if (this.showSampleEvaluation) {
                this.showSampleEvaluation = false
                return
            }

            let s = null
            if (this.selectedTab === 1 && this.selectedSamples.length > 0) { // Samples
                s = this.samples.filter(s => s.id === this.selectedSamples[0])[0]

                if (s != null && s.analysisConclusion == null) {
                    let result = await apiMachines.getMeasurements(s.machine.id)
                    if (result.code === 0) {
                        let a = result.data.filter(a => a.sample.id === this.selectedSamples[0])[0]
                        s.analysisConclusion = { actions: '', diagnosis: a.diagnosis != null ? a.diagnosis : '', generalConclusion: false, sampleId: 0 }
                    }
                    else {
                        s.analysisConclusion = { actions: '', diagnosis: '', generalConclusion: false, sampleId: 0 }
                    }
                }
            }
            else if (this.selectedTab === 2 && this.selectedAnalyze !== -1) { //Analyzes
                let a = this.analyzes.filter(a => a.id === this.selectedAnalyze)[0]
                s = this.samples.filter(b => b.id === a.sample.id)[0]

                if (s != null && s.analysisConclusion == null) {
                    s.analysisConclusion = { actions: '', diagnosis: a.diagnosis != null ? a.diagnosis : '', generalConclusion: false, sampleId: 0 }
                }
            }
            if (s != null) {
                this.sampleForEvaluation = s
                this.showSampleEvaluation = true
            }
        },
        async sampleEvaluationClosed(result, data) {
            this.showSampleEvaluation = false

            if (result >= 0) { // Save
                data.analysisConclusion.sampleId = data.id
                let res = await apiSamples.concludeSample(data.id, data.analysisConclusion)
                if (res.code === 0) {
                    this.samples.filter(s => s.id === data.id)[0].analysisConclusion = data.analysisConclusion
                }

                if (result >= 1) { // Create protocol
                    this.messageBoxText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample047 != null ? this.languageStrings.ui.sample047 : 'Generování protokolu...'
                    this.showMessageBoxButtons = false
                    this.showMessageBox = true
                    let res = await apiSamples.createProtocol(data.id)
                    if (res.code === 0) {
                        this.getSampleAttachments(data.id)
                    }
                    this.showMessageBox = false
                    this.showMessageBoxButtons = true

                    if (result == 2) { // Send and lock sample                        
                        await apiSamples.sendSample(data.id)
                        res = await apiSamples.changeSampleLock(data.id, true)
                        if (res.code === 0) {
                            let sa = this.samples.filter(s => s.id === data.id)
                            if (sa.length > 0) {
                                sa[0].locked = true
                            }
                            let an = this.analyzes.filter(a => a.sample.id === data.id)
                            if (an.length > 0) {
                                an[0].sample.locked = true
                            }
                        }
                    }

                    if (this.selectedTab === 2) {
                        this.refreshTab()
                    }
                }
            }

            this.sampleForEvaluation = null
        },
        sendSample() {
            let s = null
            if (this.selectedTab === 1 && this.selectedSamples.length > 0) { // Samples
                s = this.samples.filter(s => this.selectedSamples.includes(s.id))
            }
            else if (this.selectedTab === 2 && this.selectedAnalyze !== -1) { //Analyzes
                s = this.samples.filter(b => b.id === this.analyzes.filter(a => a.id === this.selectedAnalyze)[0].sample.id)
            }
            if (s != null && s.length > 0) {
                this.messageBoxQuestionText = (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample038 != null ? this.languageStrings.ui.sample038 : 'Odeslat vzorek') + '?'
                this.messageBoxType = 7
                this.showMessageBoxQuestion = true
                this.messageBoxData = { ids: s.map(a => a.id) }
            }
        },
        lockSample() {
            let s = null
            if (this.selectedTab === 1 && this.selectedSamples.length > 0) { // Samples
                s = this.samples.filter(s => this.selectedSamples.includes(s.id))
            }
            else if (this.selectedTab === 2 && this.selectedAnalyze !== -1) { //Analyzes
                s = this.samples.filter(b => b.id === this.analyzes.filter(a => a.id === this.selectedAnalyze)[0].sample.id)
            }
            if (s != null && s.length > 0) {
                if (s[0].locked) {
                    if (this.role === 4) { //Admin
                        this.messageBoxQuestionText = (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample040 != null ? this.languageStrings.ui.sample040 : 'Odemknout vzorek') + '?'
                        this.messageBoxType = 5
                        this.showMessageBoxQuestion = true
                        this.messageBoxData = { ids: s.map(a => a.id) }
                    }
                }
                else {
                    this.messageBoxQuestionText = (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample039 != null ? this.languageStrings.ui.sample039 : 'Zamknout vzorek') + '?'
                    this.messageBoxType = 6
                    this.showMessageBoxQuestion = true
                    this.messageBoxData = { ids: s.map(a => a.id) }
                }
            }
        },
        markSampleToDownload() {
            let s = null
            if (this.selectedTab === 1 && this.selectedSamples.length > 0) { // Samples
                s = this.samples.filter(s => this.selectedSamples.includes(s.id))
            }
            else if (this.selectedTab === 2 && this.selectedAnalyze !== -1) { //Analyzes
                s = this.samples.filter(b => b.id === this.analyzes.filter(a => a.id === this.selectedAnalyze)[0].sample.id)
            }
            if (s != null && s.length > 0) {
                for (let i = 0; i < s.length; i++) {
                    apiSamples.markSampleToDownloadFromALS(s[i].id, s[i])
                }
            }
        },

        // Analyzes
        async getMeasurements() {
            let sf = this.selectedFolder
            let sm = this.selectedMachines

            this.resetAnalyzes()

            if (this.selectedFolder > 0) {
                if (this.selectedMachines.length > 0) {
                    let a = []
                    let resultCode = 0
                    for (let i = 0; i < this.selectedMachines.length; i++) {
                        let result = await apiMachines.getMeasurements(this.selectedMachines[i])
                        if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                            a.push(...result.data)
                        }
                        else {
                            resultCode = result.code
                        }
                    }
                    if (resultCode === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                        this.analyzes = a.sort((a, b) => b.sample.collectionDate > a.sample.collectionDate ? 1 : -1)

                        this.getAnalyzesFromResults()
                        this.getAnalyzesAttachmentsAndInfoFromSample()
                    }
                }
                else {
                    let ss = this.selectedSamples
                    if (this.selectedSamples.length > 0) {
                        let a = []
                        let resultCode = 0
                        let result = await apiMeasurements.getSelectedSamples(this.selectedSamples)
                        if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                            a.push(...result.data)
                        }
                        else {
                            resultCode = result.code
                        }
                        if (resultCode === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length && ss.length === this.selectedSamples.length && ss.join() === this.selectedSamples.join()) {
                            this.analyzes = a.sort((a, b) => b.sample.collectionDate > a.sample.collectionDate ? 1 : -1)

                            this.getAnalyzesFromResults()
                            this.getAnalyzesAttachmentsAndInfoFromSample()
                        }
                    }
                }
            }
            else if (this.selectedFolder === -3) {
                let result = await apiMachines.getMeasurements(-3)
                if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                    this.analyzes = result.data
                    this.tempAnalyzes.push(...this.analyzes)
                }
            }
        },
        setSelectedAnalyze(value) {
            if (value !== this.selectedAnalyze) {
                this.selectedAnalyze = value

                this.getAnalyzePhoto(value)
            }
        },
        getAnalyzesFromResults() {
            this.analyzesFromResults = Array.from(new Set(this.analyzes.map(m => m.results.filter(r => r.analysis != null && r.analysis.internalKey != 'FiltImg' && r.analysis.internalKey != 'xFiltImg' && r.analysis.internalKey != 'xFTIR').map(r => r.analysis)).flat(1).map(JSON.stringify))).map(JSON.parse).sort((a, b) => a.group - b.group)
        },
        getAnalyzeName(internalKey, idAls) {
            if (this.languageStrings == null || this.languageStrings.analyses == null)
                return internalKey

            if (this.languageStrings.analyses[`${internalKey}_pointer`] != null)
                return this.languageStrings.analyses[`${internalKey}_pointer`]
            else
                return idAls
        },
        async addGraphData(folderId, machineId, goId) {
            if (folderId !== this.selectedFolder || machineId != this.selectedMachines[0] || goId >= this.graphOrder.length) {
                return
            }

            let labels = []
            let data = []
            let data2 = []
            let data3 = []

            for (let k = 0; k < this.analyzes.length; k++) {
                labels.unshift(this.formatDate(this.analyzes[k].sample.collectionDate))

                let r = this.analyzes[k].results.filter(r => r.analysis != null).find(r => r.analysis.internalKey === this.graphOrder[goId].internalKey)
                if (this.graphOrder[goId].internalKey !== 'ISO_4406') {
                    data.unshift((r == null || Number.isNaN(r.value.replace('>', '').replace('<', '')) ? null : r.value.replace('>', '').replace('<', '')))
                }
                else {
                    if (r != null) {
                        let vals = r.value.replaceAll('>', '').replaceAll('<', '').split('/')
                        data.unshift(Number.isNaN(vals[0]) ? null : vals[0])
                        data2.unshift(Number.isNaN(vals[1]) ? null : vals[1])
                        data3.unshift(Number.isNaN(vals[2]) ? null : vals[2])
                    }
                    else {
                        data.unshift(null)
                        data2.unshift(null)
                        data3.unshift(null)
                    }
                }
            }
            
            if (this.graphOrder[goId].internalKey !== 'ISO_4406') {
                this.graphData.push({
                    title: this.getAnalyzeName(this.graphOrder[goId].internalKey, this.graphOrder[goId].idAls),
                    chartData: {
                        labels: labels,
                        datasets: [
                            {
                                data: data,
                                borderColor: '#4169E1',
                                backgroundColor: '#4169E1',
                                spanGaps: true
                            }
                        ]
                    }
                })
            }
            else {
                this.graphData.push({
                    title: this.getAnalyzeName(this.graphOrder[goId].internalKey, this.graphOrder[goId].idAls),
                    chartData: {
                        labels: labels,
                        datasets: [
                            {
                                data: data,
                                borderColor: '#4169E1',
                                backgroundColor: '#4169E1',
                                spanGaps: true
                            },
                            {
                                data: data2,
                                borderColor: '#ffa500',
                                backgroundColor: '#ffa500',
                                spanGaps: true
                            },
                            {
                                data: data3,
                                borderColor: '#C1272D',
                                backgroundColor: '#C1272D',
                                spanGaps: true
                            }
                        ]
                    }
                })
            }
            
            if (goId + 1 < this.graphOrder.length) {
                setTimeout(() => { this.addGraphData(this.selectedFolder, this.selectedMachines[0], goId + 1) }, 50)
            }
        },
        async getGraphsData() {
            this.graphData = []

            if (this.graphOrder.length > 0) {
                setTimeout(() => { this.addGraphData(this.selectedFolder, this.selectedMachines[0], 0) }, 50)
            }
        },
        async getAnalyzesAttachmentsAndInfoFromSample() {
            let sf = this.selectedFolder
            let sm = this.selectedMachines

            if (this.samples == null)
                return

            for (let i = 0; i < this.analyzes.length; i++) {
                let ind = this.samples.findIndex(s => s.id === this.analyzes[i].sample.id)

                if (this.samples[ind].machineWorkingHours != null)
                    this.analyzes[i].machineWorkingHours = this.samples[ind].machineWorkingHours

                if (this.samples[ind].analysisConclusion != null) {
                    if (this.samples[ind].analysisConclusion.diagnosis != null)
                        this.analyzes[i].diagnosis = this.samples[ind].analysisConclusion.diagnosis
                    if (this.samples[ind].analysisConclusion.actions != null)
                        this.analyzes[i].actions = this.samples[ind].analysisConclusion.actions
                    else
                        this.analyzes[i].actions = ''
                }
                else {
                    this.analyzes[i].actions = ''
                }

                let result = await apiSamples.getSamplesAttachments(this.analyzes[i].sample.id)

                // Check array changes
                if (this.analyzes.length <= i || this.samples.length <= ind)
                    return

                if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length && result.data.length > 0) {
                    if (this.role === 1 && result.data.filter(a => !a.name.startsWith('_')).length === 0) {
                        this.analyzes[i].attachments = false
                        this.samples[ind].attachments = []
                    }
                    else {
                        this.analyzes[i].attachments = true
                        this.samples[ind].attachments = this.role != 1 ? result.data : result.data.filter(a => !a.name.startsWith('_'))
                    }
                }
                else {
                    this.analyzes[i].attachments = false
                    this.samples[ind].attachments = []
                }

                this.tempAnalyzes.push(this.analyzes[i])
            }

            // Moved from getMeasurements to quicker drawing of analyzes
            this.getGraphsData()
        },
        async getAnalyzePhoto(id) {
            let sf = this.selectedFolder
            let sm = this.selectedMachines
            let sa = this.selectedAnalyze

            this.photoSrc = []
            let ia = this.analyzes.findIndex(a => a.id === id)
            if (id !== -1 && this.analyzes[ia].attachments) {
                let att = this.samples.find(s => s.id === this.analyzes[ia].sample.id).attachments.filter(a => a.name.toLowerCase().endsWith('jpg') || a.name.toLowerCase().endsWith('jpeg'))

                for (let i = 0; i < att.length; i++) {
                    let response = await apiFiles.getFileContentArrayBuffer(att[i].dataUrl)
                    if (response.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length && sa === this.selectedAnalyze) {
                        const b64 = this.arrayBufferToBase64(response.data.data);
                        const imgData = "data:" + response.data.headers['content-type'] + ";base64," + b64;
                        this.photoSrc.push(imgData)
                    }
                }
            }
        },
        async showAnalyzeAttachments() {
            await this.showAttachments()
        },

        // Attachments
        async showAttachments() {
            this.attachmentsToDownload = []

            switch (this.selectedTab) {
                case 1:
                    for (let i = 0; i < this.selectedSamples.length; i++) {
                        if (this.role === 1)
                            this.attachmentsToDownload.push(...this.samples.find(s => s.id === this.selectedSamples[i]).attachments.filter(a => !a.name.startsWith('_')))
                        else
                            this.attachmentsToDownload.push(...this.samples.find(s => s.id === this.selectedSamples[i]).attachments)
                    }
                    break;

                case 2:
                    if (this.selectedAnalyze !== -1) {
                        if (this.role === 1)
                            this.attachmentsToDownload.push(...this.samples.find(s => s.id === this.analyzes.find(a => a.id === this.selectedAnalyze).sample.id).attachments.filter(a => !a.name.startsWith('_')))
                        else
                            this.attachmentsToDownload.push(...this.samples.find(s => s.id === this.analyzes.find(a => a.id === this.selectedAnalyze).sample.id).attachments)
                    }
                    break;
            }

            this.showDownloadForm = true
            await nextTick()
            document.getElementById('downloadForm').focus()
        },
        closeDownloadForm() {
            this.showDownloadForm = false
        },
        handleDownloadFormKeyDown(event) {
            if (event.key === 'Escape')
                this.showDownloadForm = false
        },
        async downloadAttachments(ids) {
            let att = this.attachmentsToDownload.find(a => a.id === ids[0])
            let response = await apiFiles.getFileContentBlob(att.dataUrl)
            if (response.code === 0) {
                const blob = new Blob([response.data.data], { type: response.data.headers['content-type'] })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(blob)
                link.target = '_blank'
                link.download = att.name
                link.click()
                URL.revokeObjectURL(link.href)
            }
        },
        async downloadAllAttachments() {
            if (this.attachmentsToDownload.length > 0) {
                let atts = this.attachmentsToDownload.map(a => a.id.toString())
                let response = await apiFiles.getZipContentBlob(this.attachmentsToDownload[0].dataUrl.slice(0, this.attachmentsToDownload[0].dataUrl.lastIndexOf('/')), atts)
                if (response.code === 0) {
                    const blob = new Blob([response.data.data], { type: response.data.headers['content-type'] })
                    const link = document.createElement('a')
                    link.href = URL.createObjectURL(blob)
                    link.target = '_blank'
                    link.download = "attachments.zip"
                    link.click()
                    URL.revokeObjectURL(link.href)
                }
            }
        },

        // Edit form
        async editFormClosed(result, data) {
            this.showEditForm = false

            if (result === 0) {
                switch (data.type) {
                    case 1: // Folders
                        if (data.id === -1) { // new
                            let result = await apiCustomers.postCustomer({
                                name: data.rows[0].value,
                                type: data.rows[12].value ? { id: 7, type: 7 } : this.selectedFolder > 0 ? { id: 6, type: 6 } : { id: 1, type: 1 },
                                region: data.rows[1].value > 0 ? { id: data.rows[1].value } : null,
                                parent: this.selectedFolder > 0 ? { id: this.selectedFolder } : null,
                                children: [],
                                street: data.rows[2].value,
                                city: data.rows[3].value,
                                zipCode: data.rows[4].value,
                                country: data.rows[5].value,
                                firstName: data.rows[6].value,
                                lastName: data.rows[7].value,
                                phoneNumber: data.rows[8].value,
                                email: data.rows[9].value,
                                description: data.rows[10].value,
                                kleentek: data.rows[11].value,
                                tagLinde: data.rows.length > 12 && this.isSelectedFolderLinde ? data.rows[13].value : '',
                                grafanaUrl: data.rows.length > 12 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[14].value : data.rows[13].value) : '',
                                internalKey: data.rows.length > 12 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[15].value : data.rows[14].value) : ''
                            })
                            if (result.code === 0) {
                                if (this.selectedFolder > 0) { // folder
                                    if (this.folder.children == null) {
                                        this.folder.children = []
                                    }
                                    this.folder.children.push({ id: result.data.id, name: data.rows[0].value, internalKey: data.rows[0].value.substring(0, 15).replaceAll(' ', '_').replaceAll(',', '').replaceAll('.', ''), order: 10, parentId: this.folder.id, grafanaUrl: result.data.grafanaUrl })
                                }
                                else { // customer
                                    this.folders.push({ id: result.data.id, name: data.rows[0].value, internalKey: data.rows[0].value.substring(0, 15).replaceAll(' ', '_').replaceAll(',', '').replaceAll('.', ''), order: 10, grafanaUrl: result.data.grafanaUrl })
                                }
                                await nextTick()
                                this.setSelectedFolder(result.data.id)
                            }
                        }
                        else { // edit
                            let result = await apiCustomers.putCustomer(
                                data.id,
                                {
                                    id: data.id,
                                    name: data.rows[0].value,
                                    type: data.rows[12].value ? { id: 7, type: 7 } : this.folder.parentId != null ? { id: 6, type: 6 } : { id: 1, type: 1 },
                                    region: data.rows[1].value > 0 ? { id: data.rows[1].value } : null,
                                    parent: this.folder.parentId != null ? { id: this.folder.parentId } : null,
                                    children: this.folder.children,
                                    street: data.rows[2].value,
                                    city: data.rows[3].value,
                                    zipCode: data.rows[4].value,
                                    country: data.rows[5].value,
                                    firstName: data.rows[6].value,
                                    lastName: data.rows[7].value,
                                    phoneNumber: data.rows[8].value,
                                    email: data.rows[9].value,
                                    description: data.rows[10].value,
                                    kleentek: data.rows[11].value,
                                    tagLinde: data.rows.length > 12 && this.isSelectedFolderLinde ? data.rows[13].value : this.folder.tagLinde,
                                    grafanaUrl: data.rows.length > 12 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[14].value : data.rows[13].value) : this.folder.grafanaUrl,
                                    internalKey: data.rows.length > 12 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[15].value : data.rows[14].value) : this.folder.internalKey
                                }
                            )
                            if (result.code === 0) {
                               this.folder.name = data.rows[0].value
                               this.folder.internalKey = data.rows[0].value.substring(0, 15).replaceAll(' ', '_').replaceAll(',', '').replaceAll('.', '')
                               this.folder.grafanaUrl = result.data.grafanaUrl

                               this.grafanaLink = this.folder.grafanaUrl
                            }
                        }
                        break

                        
                    case 2: // Machines
                        if (data.id === -1) { // new
                            let result = await apiMachines.postMachine({
                                name: data.rows[0].value,
                                folder: { id: this.selectedFolder, name: this.folder.name, children: [] },
                                customerId: data.rows[1].value,
                                producer: data.rows[2].value,
                                type: data.rows[3].value,
                                model: data.rows[4].value,
                                yearProduced: data.rows[5].value.length > 0 ? Number(data.rows[5].value) : null,
                                volume: data.rows[6].value.length > 0 ? Number(data.rows[6].value) : null,
                                oilProducer: data.rows[7].value,
                                oilBrand: data.rows[8].value,
                                oilType: data.rows[9].value > 0 ? { id: Number(data.rows[9].value), class: this.localLanguageFiles.oilTypes.find(ot => ot.id === Number(data.rows[9].value)).name } : null,
                                machineOil: data.rows[10].value > 0 ? { id: Number(data.rows[10].value), name: this.localLanguageFiles.oilVTs.find(ov => ov.id === Number(data.rows[10].value)).name } : null,
                                medium: data.rows[11].value,
                                description: data.rows[12].value,
                                uinals: data.rows.length > 13 ? data.rows[13].value : '',
                                tagLinde: data.rows.length > 14 ? data.rows[14].value : '',
                                state: '-1;4',
                                stateTwo: -1,
                                grafanaUrl: data.rows.length > 13 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[15].value : data.rows[14].value) : '',
                                internalKey: data.rows.length > 13 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[16].value : data.rows[15].value) : ''
                            })
                            if (result.code === 0) {
                                this.machines.unshift(result.data)
                                await nextTick()
                                this.setSelectedMachines([ result.data.id ])
                            }
                        }
                        else { // edit
                            let result = await apiMachines.putMachine(
                                this.selectedMachines[0],
                                {
                                    id: this.selectedMachines[0],
                                    name: data.rows[0].value,
                                    folder: { id: this.selectedFolder, name: this.machine[0].folder.name, children: [] },
                                    customerId: data.rows[1].value,
                                    producer: data.rows[2].value,
                                    type: data.rows[3].value,
                                    model: data.rows[4].value,
                                    yearProduced: data.rows[5].value.toString().length > 0 ? Number(data.rows[5].value) : null,
                                    volume: data.rows[6].value.toString().length > 0 ? Number(data.rows[6].value) : null,
                                    oilProducer: data.rows[7].value,
                                    oilBrand: data.rows[8].value,
                                    oilType: data.rows[9].value > 0 ? { id: Number(data.rows[9].value), class: this.localLanguageFiles.oilTypes.find(ot => ot.id === Number(data.rows[9].value)).name } : null,
                                    machineOil: data.rows[10].value > 0 ? { id: Number(data.rows[10].value), name: this.localLanguageFiles.oilVTs.find(ov => ov.id === Number(data.rows[10].value)).name } : null,
                                    medium: data.rows[11].value,
                                    description: data.rows[12].value,
                                    uinals: data.rows.length > 13 ? data.rows[13].value : this.machine[0].uinals,
                                    tagLinde: data.rows.length > 14 ? data.rows[14].value : this.machine[0].tagLinde,
                                    state: this.machine[0].state,
                                    stateTwo: this.machine[0].stateTwo,
                                    uid: this.machine[0].uid,
                                    grafanaUrl: data.rows.length > 13 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[15].value : data.rows[14].value) : this.machine[0].grafanaUrl,
                                    internalKey: data.rows.length > 13 && this.role === 4 ? (this.isSelectedFolderLinde ? data.rows[16].value : data.rows[15].value) : this.machine[0].internalKey
                                }
                            )
                            if (result.code === 0) {
                                this.machines[this.machines.findIndex(m => m.id === this.selectedMachines[0])] = result.data

                                this.grafanaLink = this.machine[0].grafanaUrl
                            }
                        }
                        break


                    case 3: // Samples
                        if (data.id === -1) { // new
                            let m = this.machine[0]
                            let result = await apiSamples.postSample({
                                sif: data.rows[0].value,
                                machine: { id: m.id, name: m.name, internalKey: m.internalKey, folder: m.folder },
                                alsNumber: data.rows[1].value,
                                collectionDate: data.rows[2].value != null && data.rows[2].value.length > 0 ? data.rows[2].value : null,
                                deliveryPlace: data.rows[3].value != 0 ? { id: data.rows[3].value } : null,
                                receivingDate: data.rows[4].value != null && data.rows[4].value.length > 0 ? data.rows[4].value : null,
                                processingDate: data.rows[5].value != null && data.rows[5].value.length > 0 ? data.rows[5].value : null,
                                exchangeDate: data.rows[6].value != null && data.rows[6].value.length > 0 ? data.rows[6].value : null,
                                exchanged: data.rows[7].value,
                                supplemented: data.rows[8].value.toString().length > 0 ? Number(data.rows[8].value.toString().replace(',', '.')) : null,
                                machineWorkingHours: data.rows[9].value.toString().length > 0 ? Number(data.rows[9].value) : null,
                                oilWorkingHours: data.rows[10].value.toString().length > 0 ? Number(data.rows[10].value) : null,
                                collectionPlace: data.rows[11].value != null ? data.rows[11].value : '',
                                collectionPersonName: data.rows[12].value != null ? data.rows[12].value : '',
                                oilTemperature: data.rows[13].value.toString().length > 0 ? Number(data.rows[13].value.toString().replace(',', '.')) : null,
                                environmentTemperature: data.rows[14].value.toString().length > 0 ? Number(data.rows[14].value.toString().replace(',', '.')) : null,
                                description: data.rows[15].value != null ? data.rows[15].value : ''
                            })
                            if (result.code === 0) {
                                this.samples.unshift(result.data)
                            }
                        }
                        else { // edit
                            let s = this.samples.find(s => s.id === data.id)
                            let result = await apiSamples.putSample(
                                data.id,
                                {
                                    id: data.id,
                                    sif: data.rows[0].value,
                                    machine: s.machine,
                                    alsNumber: data.rows[1].value,
                                    collectionDate: data.rows[2].value != null && data.rows[2].value.length > 0 ? data.rows[2].value : null,
                                    deliveryPlace: data.rows[3].value != 0 ? { id: data.rows[3].value } : null,
                                    receivingDate: data.rows[4].value != null && data.rows[4].value.length > 0 ? data.rows[4].value : null,
                                    processingDate: data.rows[5].value != null && data.rows[5].value.length > 0 ? data.rows[5].value : null,
                                    exchangeDate: data.rows[6].value != null && data.rows[6].value.length > 0 ? data.rows[6].value : null,
                                    exchanged: data.rows[7].value,
                                    supplemented: data.rows[8].value.toString().length > 0 ? Number(data.rows[8].value.toString().replace(',', '.')) : null,
                                    machineWorkingHours: data.rows[9].value.toString().length > 0 ? Number(data.rows[9].value) : null,
                                    oilWorkingHours: data.rows[10].value.toString().length > 0 ? Number(data.rows[10].value) : null,
                                    collectionPlace: data.rows[11].value != null ? data.rows[11].value : '',
                                    collectionPersonName: data.rows[12].value != null ? data.rows[12].value : '',
                                    oilTemperature: data.rows[13].value.toString().length > 0 ? Number(data.rows[13].value.toString().replace(',', '.')) : null,
                                    environmentTemperature: data.rows[14].value.toString().length > 0 ? Number(data.rows[14].value.toString().replace(',', '.')) : null,
                                    description: data.rows[15].value != null ? data.rows[15].value : '',
                                    evaluation: s.evaluation != null ? s.evaluation : '',
                                    order: s.order != null ? s.order : null,
                                    locked: s.locked != null ? s.locked : null,
                                    lockedDate: s.lockedDate != null ? s.lockedDate : null,
                                    lockedPerson: s.lockedPerson != null ? s.lockedPerson : null,
                                    shippingDate: s.shippingDate != null ? s.shippingDate : null,
                                    shippingPerson: s.shippingPerson != null ? s.shippingPerson : null,
                                    analysisConclusion: s.analysisConclusion != null ? s.analysisConclusion : null,
                                    attachments: s.attachments != null ? s.attachments : null
                                }
                            )
                            if (result.code === 0) {
                                this.samples[this.samples.findIndex(s => s.id === data.id)] = result.data
                            }
                        }
                        break


                    case 4: // Events
                        if (data.id === -1) { // new
                            let result = await apiEvents.postEvent({
                                machine: { id: this.selectedMachines[0], name: '', internalKey: '', folder: { id: this.selectedFolder, name: '', children: [] } },
                                dateFrom: data.rows[0].value,
                                dateTo: data.rows[1].value != null && data.rows[1].value.length > 0 ? data.rows[1].value : null,
                                category: { internalKey: data.rows[2].value },
                                description: data.rows[3].value
                            })
                            if (result.code === 0) {
                                this.events.push(result.data)
                            }
                        }
                        else { // edit
                            let result = await apiEvents.putEvent(
                                this.selectedEvent,    
                                {
                                    id: this.selectedEvent,
                                    machine: { id: this.selectedMachines[0], name: '', internalKey: '', folder: { id: this.selectedFolder, name: '', children: [] } },
                                    dateFrom: data.rows[0].value,
                                    dateTo: data.rows[1].value != null && data.rows[1].value.length > 0 ? data.rows[1].value : null,
                                    category: { internalKey: data.rows[2].value },
                                    description: data.rows[3].value
                                }
                            )
                            if (result.code === 0) {
                                this.events[this.events.findIndex(e => e.id === this.selectedEvent)] = result.data
                            }
                        }
                        break


                    case 5: { // Move machine
                            let machine = this.machines.find(m => m.id === this.selectedMachines[0])
                            machine.folder = { id: data.rows[0].value, name: '', children: [] }
                            if (machine.oilType != null && machine.oilType.class == null) {
                                machine.oilType.class = ''
                            }
                            let result = await apiMachines.putMachine(machine.id, machine)
                            if (result.code === 0) {
                                this.getMachines()
                            }
                        }
                        break
                }
            }

            this.editFormData = null
        },

        // Sensors
        sensorsSelectMachine(id) {
            this.setSelectedMachines([ id ])
        },
        setSelectedSensor(s) {
            this.selectedSensor = s
        },
        saveSensorsTabState(s) {
            this.sensorsTabState = s
        },
        async addSensor() {
            let fids = this.getFolderIdsInTree(this.selectedFolder)

            let response = await apiSensors.getSensors()
            if (response.code === 0) {
                this.sensorsList = response.data.filter(s => fids.includes(s.customer.id)).sort((a, b) => a.serialNumber.localeCompare(b.serialNumber) || a.customer.name.localeCompare(b.customer.name))

                this.selectedSensorTemp = this.selectedSensor
                this.selectedSensor = null
                this.showSensorForm = true
            }
            else {
                this.sensorsList = []
            }
        },
        editSensor() {
            if (this.selectedSensor != null) {
                this.sensorsList = []
                this.selectedSensorTemp = this.selectedSensor
                this.showSensorForm = true
            }
        },
        deleteSensor() {
            if (this.selectedSensor != null) {
                this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor012 != null ? this.languageStrings.ui.sensor012 : 'Smazat senzor?'
                this.messageBoxType = 8
                this.showMessageBoxQuestion = true
            }
        },
        moveSensor(value) {
            this.sensorsList = []
            this.selectedSensorTemp = null
            this.selectedSensor = value
            this.selectedSensor.id = -1
            this.selectedSensor.machine.id = -1
            this.showSensorForm = true
        },
        async sensorFormClosed(result, data) {
            this.showSensorForm = false
            
            if (result === 0) {
                if (data.id === -1) { // new
                    let result = await apiMachines.postSensor(data)
                    if (result.code === 0) {
                        this.refreshTab()
                    }
                }
                else { // edit
                    let result = await apiMachines.putSensor(data)
                    if (result.code === 0) {
                        this.refreshTab()
                    }
                }
            }
            else {
                this.selectedSensor = this.selectedSensorTemp
                this.selectedSensorTemp = null
            }
        },

        // Events
        async getEvents() {
            let sf = this.selectedFolder
            let sm = this.selectedMachines

            if (this.selectedMachines.length > 0) {
                let e = []
                let resultCode = 0
                for (let i = 0; i < this.selectedMachines.length; i++) {
                    let result = await apiMachines.getEvents(this.selectedMachines[i])
                    if (result.code === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                        e.push(...result.data)
                    }
                    else {
                        resultCode = result.code
                    }
                }
                if (resultCode === 0 && sf === this.selectedFolder && sm.length === this.selectedMachines.length) {
                    this.events = e
                }
            }
        },
        setSelectedEvent(value) {
            this.selectedEvent = value
        },
        async getEventFormData(eId) {
            let event = this.events.find(e => e.id === eId)
            let data = {
                id: eId,
                type: 4,
                rows: [
                    {
                        name: 'date-from',
                        type: 'datetime',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event007 != null ? this.languageStrings.ui.event007 : 'Od'),
                        value: event != null ? dayjs(event.dateFrom + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:00') : dayjs().format('YYYY-MM-DDTHH:mm:00'),
                        required: true
                    },
                    {
                        name: 'date-to',
                        type: 'datetime',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event008 != null ? this.languageStrings.ui.event008 : 'Do'),
                        value: event != null && event.dateTo != null ? dayjs(event.dateTo + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:00') : null,
                        required: false
                    },
                    {
                        name: 'category',
                        type: 'options',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event005 != null ? this.languageStrings.ui.event005 : 'Kategorie'),
                        value: event != null ? event.category.internalKey : null,
                        options: [],
                        required: true
                    },
                    {
                        name: 'description',
                        type: 'text-multiline',
                        text: (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event006 != null ? this.languageStrings.ui.event006 : 'Popis'),
                        value: event != null ? event.description : '',
                        required: false
                    }
                ]
            }

            // Event categories
            let response = await apiSettings.getLanguagesEventCategories(this.userLanguage)
            if (response.code === 0) {
                data.rows[2].options = Object.entries(response.data).map(( [k, v] ) => ( { key: k, value: v } ))
                if (event == null) {
                    data.rows[2].value = data.rows[2].options[0].key
                }
            }

            return data
        },
        async addEvent() {
            this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event001 != null ? this.languageStrings.ui.event001 : 'Nová událost'
            this.editFormData = await this.getEventFormData(-1)
            this.showEditForm = true
        },
        async editEvent() {
            if (this.selectedEvent > 0) {
                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event002 != null ? this.languageStrings.ui.event002 : 'Editace události'
                this.editFormData = await this.getEventFormData(this.selectedEvent)
                this.showEditForm = true
            }
        },
        async deleteEvent() {
            if (this.selectedEvent > 0) {
                this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event010 != null ? this.languageStrings.ui.event010 : 'Smazat událost?'
                this.messageBoxType = 4
                this.showMessageBoxQuestion = true
            }
        },

        // Message boxes
        async messageBoxQuestionClosed(result, type, data) {            
            this.showMessageBoxQuestion = false

            if (result !== 0) {
                return
            }

            let res = { code: 1, data: null }
            
            switch (type) {
                case 1: { // Folders
                    let fId = this.selectedFolder > 0 ? this.folder.parentId : -10

                    await this.deleteFolderRecursive(this.folder)

                    if (fId > 0) {
                        let f = this.searchTree(this.folders, fId)
                        f.children = f.children.filter(x => x.id !== this.selectedFolder)
                    }
                    else {
                        this.folders = this.folders.filter(x => x.id !== this.selectedFolder)
                    }

                    this.selectedFolder = fId
                }
                    break

                case 2: // Machines
                    res = await apiMachines.deleteMachine(this.selectedMachines[0])
                    if (res.code === 0) {
                        this.machines = this.machines.filter(m => m.id !== this.selectedMachines[0])
                        this.selectedMachines = []
                    }
                    break

                case 3: { // Samples
                    let sid = -1
                    if (this.selectedTab === 1 && this.selectedSamples.length > 0) { // Samples
                        sid = this.selectedSamples[0]
                    }
                    else if (this.selectedTab === 2 && this.selectedAnalyze !== -1) { //Analyzes
                        sid = this.analyzes.filter(a => a.id === this.selectedAnalyze)[0].sample.id
                    }
                    res = await apiSamples.deleteSample(sid)
                    if (res.code === 0) {
                        this.samples = this.samples.filter(s => s.id !== sid)
                        this.analyzes = this.analyzes.filter(a => a.sample.id !== sid)
                        this.selectedSamples = []
                        this.selectedAnalyze = -1
                    }
                }
                    break

                case 4: // Events
                    res = await apiEvents.deleteEvent(this.selectedEvent, this.events.find(e => e.id === this.selectedEvent))
                    if (res.code === 0) {
                        this.events = this.events.filter(e => e.id !== this.selectedEvent)
                        this.selectedEvent = -1
                    }
                    break

                case 5: // Unlock sample
                    if (data != null && data.ids != null) {
                        for (let i = 0; i < data.ids.length; i++) {
                            res = await apiSamples.changeSampleLock(data.ids[i], false)
                            if (res.code === 0) {
                                let sa = this.samples.filter(s => s.id === data.ids[i])
                                if (sa.length > 0) {
                                    sa[0].locked = false
                                }
                                let an = this.analyzes.filter(a => a.sample.id === data.ids[i])
                                if (an.length > 0) {
                                    an[0].sample.locked = false
                                }
                            }
                        }
                    }
                    break

                case 6: // Lock sample
                    if (data != null && data.ids != null) {
                        for (let i = 0; i < data.ids.length; i++) {
                            res = await apiSamples.changeSampleLock(data.ids[i], true)
                            if (res.code === 0) {
                                let sa = this.samples.filter(s => s.id === data.ids[i])
                                if (sa.length > 0) {
                                    sa[0].locked = true
                                }
                                let an = this.analyzes.filter(a => a.sample.id === data.ids[i])
                                if (an.length > 0) {
                                    an[0].sample.locked = true
                                }
                            }
                        }
                    }
                    break

                case 7: // Send sample
                    if (data != null && data.ids != null) {
                        for (let i = 0; i < data.ids.length; i++) {
                            await apiSamples.sendSample(data.ids[i])
                        }
                    }
                    break

                case 8: // Sensors
                    res = await apiMachines.deleteSensor(this.selectedSensor)
                    if (res.code === 0) {
                        this.refreshTab()
                    }
                    break
            }
        },
        async messageBoxClosed() {            
            this.showMessageBox = false
        }
    }
}
</script>


<template>
    <div class="content">
        <!-- Folders pane -->
        <div :class="'customers' + (maxFolders ? ' maxed' : '') + (lockCustomers ? ' locked' : '')">
            <div class="pane-button-strip">
                <span class="strip-name">{{ folder != null ? folder.name : '' }}</span>

                <div class="left-icons button-to-hide" v-if="role > 1 && role <= 4">
                    <button class="button-strip" @click="addFolder()">
                        <img v-if="selectedFolder <= 0" src="@/assets/images/plus.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip010 != null ? this.languageStrings.ui.tooltip010 : 'Nový zákazník'" />
                        <img v-else src="@/assets/images/plus.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip013 != null ? this.languageStrings.ui.tooltip013 : 'Nová složka'" />
                    </button>
                    <button class="button-strip" @click="editFolder()">
                        <img v-if="selectedFolder > 0 && folder.parentId != null" src="@/assets/images/edit-3.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip014 != null ? this.languageStrings.ui.tooltip014 : 'Editace složky'" />
                        <img v-else src="@/assets/images/edit-3.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip011 != null ? this.languageStrings.ui.tooltip011 : 'Editace zákazníka'" />
                    </button>
                    <button v-if="role === 4" class="button-strip" @click="deleteFolder()">
                        <img v-if="selectedFolder > 0 && folder.parentId != null" src="@/assets/images/trash-2.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip015 != null ? this.languageStrings.ui.tooltip015 : 'Smazání složky'" />
                        <img v-else src="@/assets/images/trash-2.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip012 != null ? this.languageStrings.ui.tooltip012 : 'Smazání zákazníka'" />
                    </button>
                </div>

                <button class="button-strip button-to-hide" @click="refreshFolders()">
                    <img src="@/assets/images/refresh-cw.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip001 != null ? this.languageStrings.ui.tooltip001 : 'Obnovit'" />
                </button>

                <button class="button-strip button-to-hide" @click="changeMaxFolders()">
                    <img v-if="maxFolders" src="@/assets/images/minimize-2.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip003 != null ? this.languageStrings.ui.tooltip003 : 'Minimalizovat'" />
                    <img v-else src="@/assets/images/maximize-2.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip002 != null ? this.languageStrings.ui.tooltip002 : 'Maximalizovat'" />
                </button>

                <button v-if="!maxFolders" class="button-strip chevrons-left-right" @click="changeLockCustomers()">
                    <img v-if="lockCustomers" src="@/assets/images/chevrons-left.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip005 != null ? this.languageStrings.ui.tooltip005 : 'Skrýt panel'" />
                    <img v-else src="@/assets/images/chevrons-right.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip004 != null ? this.languageStrings.ui.tooltip004 : 'Zobrazit panel'" />
                </button>

                <button v-if="!maxFolders" class="button-strip chevrons-up-down" @click="changeLockCustomers()">
                    <img v-if="lockCustomers" src="@/assets/images/chevrons-up.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip005 != null ? this.languageStrings.ui.tooltip005 : 'Skrýt panel'" />
                    <img v-else src="@/assets/images/chevrons-down.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip004 != null ? this.languageStrings.ui.tooltip004 : 'Zobrazit panel'" />
                </button>
            </div>

            <div class="folders">
                <FolderComponent v-for="folder in folders" :key="folder.id" :folder="folder" @selected-folder="setSelectedFolder" @double-click="folderDoubleClick" :selected-item="selectedFolder" />
            </div>

            <span class="strip-name-vertical" @click.prevent="setSelectedFolder(folder.id)">{{ folder != null ? folder.name : '' }}</span>
        </div>
        <!-- Folders pane -->

        <!-- Machines pane -->
        <div :class="'machines' + (maxMachines ? ' maxed' : '') + (lockMachines ? ' locked' : '')">
            <div class="pane-button-strip">
                <span class="strip-name">{{ machine.length > 0 ? machine[0].name : '' }}</span>

                <div class="left-icons button-to-hide" v-if="role >= 1 && role <= 4">
                    <button class="button-strip" @click="addMachine()">
                        <img src="@/assets/images/plus.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip006 != null ? this.languageStrings.ui.tooltip006 : 'Nový stroj'" />
                    </button>
                    <button class="button-strip" @click="editMachine()">
                        <img src="@/assets/images/edit-3.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip007 != null ? this.languageStrings.ui.tooltip007 : 'Editace stroje'" />
                    </button>
                    <button v-if="role === 4" class="button-strip" @click="deleteMachine()">
                        <img src="@/assets/images/trash-2.svg" :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip009 != null ? this.languageStrings.ui.tooltip009 : 'Smazání stroje'" />
                    </button>
                    <button v-if="role === 4" class="button-strip" @click="moveMachine()">
                        <img src="@/assets/images/move.svg" :title="'Přesun stroje'" />
                    </button>
                </div>

                <button class="button-strip button-to-hide" @click="refreshMachines()">
                    <img src="@/assets/images/refresh-cw.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip001 != null ? this.languageStrings.ui.tooltip001 : 'Obnovit'" />
                </button>

                <button class="button-strip button-to-hide" @click="changeMaxMachines()">
                    <img v-if="maxMachines" src="@/assets/images/minimize-2.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip003 != null ? this.languageStrings.ui.tooltip003 : 'Minimalizovat'" />
                    <img v-else src="@/assets/images/maximize-2.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip002 != null ? this.languageStrings.ui.tooltip002 : 'Maximalizovat'" />
                </button>

                <button v-if="!maxMachines" class="button-strip chevrons-left-right" @click="changeLockMachines()">
                    <img v-if="lockMachines" src="@/assets/images/chevrons-left.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip005 != null ? this.languageStrings.ui.tooltip005 : 'Skrýt panel'" />
                    <img v-else src="@/assets/images/chevrons-right.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip004 != null ? this.languageStrings.ui.tooltip004 : 'Zobrazit panel'" />
                </button>

                <button v-if="!maxMachines" class="button-strip chevrons-up-down" @click="changeLockMachines()">
                    <img v-if="lockMachines" src="@/assets/images/chevrons-up.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip005 != null ? this.languageStrings.ui.tooltip005 : 'Skrýt panel'" />
                    <img v-else src="@/assets/images/chevrons-down.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip004 != null ? this.languageStrings.ui.tooltip004 : 'Zobrazit panel'" />
                </button>
            </div>

            <div class="machines-table">
                <MachinesComponent :machines="machines" @selected-machines="setSelectedMachines" :selected-items="selectedMachines" @double-click="machineDoubleClick" :language-strings="languageStrings" :role="role" :is-linde="isSelectedFolderLinde" :local-languages="localLanguageFiles" />
            </div>

            <span class="strip-name-vertical">{{ machine.length > 0 ? machine[0].name : '' }}</span>
        </div>
        <!-- Machines pane -->

        <!-- Tabs - samples, analyzes, sensors, events, machine info -->
        <div :class="'tabs' + (maxTabs ? ' maxed' : '')">
            <div class="menu-tabs">
                <div class="menu-tabs-ul-div">
                    <ul>
                        <li class="menu-tabs-displayed-tab">
                            <span>
                                <img v-if="selectedTab === 2" src="@/assets/images/trello.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu006 != null ? languageStrings.ui.menu006 : 'Analýzy'" />
                                <img v-else-if="selectedTab === 3" src="@/assets/images/bar-chart-2.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu007 != null ? languageStrings.ui.menu007 : 'Senzory'" />
                                <img v-else-if="selectedTab === 4" src="@/assets/images/alert-triangle.svg" :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu008 != null ? languageStrings.ui.menu008 : 'Události'" />
                                <img v-else-if="selectedTab === 5" src="@/assets/images/info.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu009 != null ? languageStrings.ui.menu009 : 'Detail stroje'" />
                                <img v-else src="@/assets/images/list.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu005 != null ? languageStrings.ui.menu005 : 'Vzorky'" />
                            </span>
                        </li>

                        <li :class="selectedTab === 1 ? 'selected' : ''">
                            <span @click="selectTab(1)">
                                <img src="@/assets/images/list.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu005 != null ? languageStrings.ui.menu005 : 'Vzorky'" />
                                <span>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu005 != null ? languageStrings.ui.menu005 : 'Vzorky' }}</span>
                            </span>
                        </li>
                        <li :class="selectedTab === 2 ? 'selected' : ''">
                            <span @click="selectTab(2)">
                                <img src="@/assets/images/trello.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu006 != null ? languageStrings.ui.menu006 : 'Analýzy'" />
                                <span>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu006 != null ? languageStrings.ui.menu006 : 'Analýzy' }}</span>
                            </span>
                        </li>
                        <li :class="selectedTab === 3 ? 'selected' : ''">
                            <span @click="selectTab(3)">
                                <img src="@/assets/images/bar-chart-2.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu007 != null ? languageStrings.ui.menu007 : 'Senzory'" />
                                <span>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu007 != null ? languageStrings.ui.menu007 : 'Senzory' }}</span>
                            </span>
                        </li>
                        <li :class="selectedTab === 4 ? 'selected' : ''">
                            <span @click="selectTab(4)">
                                <img src="@/assets/images/alert-triangle.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu008 != null ? languageStrings.ui.menu008 : 'Události'" />
                                <span>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu008 != null ? languageStrings.ui.menu008 : 'Události' }}</span>
                            </span>
                        </li>
                        <li :class="selectedTab === 5 ? 'selected' : ''">
                            <span @click="selectTab(5)">
                                <img src="@/assets/images/info.svg" 
                                    :title="languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu009 != null ? languageStrings.ui.menu009 : 'Detail stroje'" />
                                <span>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.menu009 != null ? languageStrings.ui.menu009 : 'Detail stroje' }}</span>
                            </span>
                        </li>
                    </ul>
                </div>

                <img class="tabs-icon" src="@/assets/images/paperclip.svg" @click="showAttachments()" 
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip008 != null ? this.languageStrings.ui.tooltip008 : 'Přílohy'" />
                <img class="tabs-icon" src="@/assets/images/refresh-cw.svg" @click="refreshTab()" 
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip001 != null ? this.languageStrings.ui.tooltip001 : 'Obnovit'" />
                
                <img v-if="(selectedTab === 1 || selectedTab === 2) && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-icon" src="@/assets/images/plus.svg" @click="addSample()" 
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample028 != null ? this.languageStrings.ui.sample028 : 'Nový vzorek'" />
                <img v-if="(selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-icon" src="@/assets/images/edit-3.svg" @click="editSample()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample029 != null ? this.languageStrings.ui.sample029 : 'Editace vzorku'" />
                <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" :class="'tabs-icon' + (this.showSampleEvaluation ? ' active' : '')" src="@/assets/images/check-square.svg" @click="evaluateSample()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample037 != null ? this.languageStrings.ui.sample037 : 'Vyhodnocení vzorku'" />
                <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-icon" src="@/assets/images/send.svg" @click="sendSample()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample038 != null ? this.languageStrings.ui.sample038 : 'Odeslat vzorek'" />
                <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-icon" src="@/assets/images/lock.svg" @click="lockSample()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample039 != null ? this.languageStrings.ui.sample039 : 'Zamknout vzorek'" />
                <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-icon" src="@/assets/images/bookmark.svg" @click="markSampleToDownload()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample041 != null ? this.languageStrings.ui.sample041 : 'Aktualizovat informace z ALS'" />
                <img v-if="role === 4 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-icon" src="@/assets/images/trash-2.svg" @click="deleteSample()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample030 != null ? this.languageStrings.ui.sample030 : 'Smazat vzorek'" />

                <img v-if="role > 1 && selectedTab === 3 && selectedFolder > 0" class="tabs-icon" src="@/assets/images/plus.svg" @click="addSensor()" 
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor010 != null ? this.languageStrings.ui.sensor010 : 'Nový senzor'" />
                <img v-if="role > 1 && selectedTab === 3 && selectedFolder > 0" class="tabs-icon" src="@/assets/images/edit-3.svg" @click="editSensor()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor011 != null ? this.languageStrings.ui.sensor011 : 'Editace senzoru'" />
                <img v-if="role > 1 && selectedTab === 3 && selectedFolder > 0" class="tabs-icon" src="@/assets/images/trash-2.svg" @click="deleteSensor()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor023 != null ? this.languageStrings.ui.sensor023 : 'Smazat senzor'" />

                <img v-if="selectedTab === 4 && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-icon" src="@/assets/images/plus.svg" @click="addEvent()" 
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event001 != null ? this.languageStrings.ui.event001 : 'Nová událost'" />
                <img v-if="selectedTab === 4 && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-icon" src="@/assets/images/edit-3.svg" @click="editEvent()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event002 != null ? this.languageStrings.ui.event002 : 'Editace události'" />
                <img v-if="selectedTab === 4 && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-icon" src="@/assets/images/trash-2.svg" @click="deleteEvent()"
                    :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event003 != null ? this.languageStrings.ui.event003 : 'Smazat událost'" />

                <img class="tabs-more" src="@/assets/images/more-horizontal.svg" @mouseover="showTabSubMenu1 = true" @mouseleave="triggerTabSubMenuLeave1" />
                <div v-if="showTabSubMenu1 || showTabSubMenu2" class="tabs-submenu" @mouseover="showTabSubMenu2 = true" @mouseleave="triggerTabSubMenuLeave2">
                    <img class="tabs-more" src="@/assets/images/paperclip.svg" @click="showAttachments()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip008 != null ? this.languageStrings.ui.tooltip008 : 'Přílohy'" />
                    <img class="tabs-more" src="@/assets/images/refresh-cw.svg" @click="refreshTab()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip001 != null ? this.languageStrings.ui.tooltip001 : 'Obnovit'" />
                
                    <img v-if="(selectedTab === 1 || selectedTab === 2) && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-more" src="@/assets/images/plus.svg" @click="addSample()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample028 != null ? this.languageStrings.ui.sample028 : 'Nový vzorek'" />
                    <img v-if="(selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-more" src="@/assets/images/edit-3.svg" @click="editSample()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample029 != null ? this.languageStrings.ui.sample029 : 'Editace vzorku'" />
                    <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" :class="'tabs-more' + (this.showSampleEvaluation ? ' active' : '')" src="@/assets/images/check-square.svg" @click="evaluateSample()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample037 != null ? this.languageStrings.ui.sample037 : 'Vyhodnocení vzorku'" />
                    <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-more" src="@/assets/images/send.svg" @click="sendSample()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample038 != null ? this.languageStrings.ui.sample038 : 'Odeslat vzorek'" />
                    <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-more" src="@/assets/images/lock.svg" @click="lockSample()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample039 != null ? this.languageStrings.ui.sample039 : 'Zamknout vzorek'" />
                    <img v-if="role > 1 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-more" src="@/assets/images/bookmark.svg" @click="markSampleToDownload()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample041 != null ? this.languageStrings.ui.sample041 : 'Aktualizovat informace z ALS'" />
                    <img v-if="role === 4 && (selectedTab === 1 || selectedTab === 2) && selectedFolder > 0" class="tabs-more" src="@/assets/images/trash-2.svg" @click="deleteSample()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sample030 != null ? this.languageStrings.ui.sample030 : 'Smazat vzorek'" />

                    <img v-if="role > 1 && selectedTab === 3 && selectedFolder > 0" class="tabs-more" src="@/assets/images/plus.svg" @click="addSensor()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor010 != null ? this.languageStrings.ui.sensor010 : 'Nový senzor'" />
                    <img v-if="role > 1 && selectedTab === 3 && selectedFolder > 0" class="tabs-more" src="@/assets/images/edit-3.svg" @click="editSensor()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor011 != null ? this.languageStrings.ui.sensor011 : 'Editace senzoru'" />
                    <img v-if="role > 1 && selectedTab === 3 && selectedFolder > 0" class="tabs-more" src="@/assets/images/trash-2.svg" @click="deleteSensor()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor023 != null ? this.languageStrings.ui.sensor023 : 'Smazat senzor'" />

                    <img v-if="selectedTab === 4 && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-more" src="@/assets/images/plus.svg" @click="addEvent()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event001 != null ? this.languageStrings.ui.event001 : 'Nová událost'" />
                    <img v-if="selectedTab === 4 && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-more" src="@/assets/images/edit-3.svg" @click="editEvent()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event002 != null ? this.languageStrings.ui.event002 : 'Editace události'" />
                    <img v-if="selectedTab === 4 && selectedFolder > 0 && selectedMachines.length > 0" class="tabs-more" src="@/assets/images/trash-2.svg" @click="deleteEvent()"
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.event003 != null ? this.languageStrings.ui.event003 : 'Smazat událost'" />
                </div>
            </div>


            <!-- Sample evaluation form -->
            <SampleEvaluationForm v-if="showSampleEvaluation && (selectedTab === 1 || selectedTab === 2)" @form-closed="sampleEvaluationClosed" :sample="sampleForEvaluation" :language-strings="languageStrings" />
            <!-- Sample evaluation form -->

            <div v-if="selectedTab === 1" :class="'samples-table' + (this.showSampleEvaluation ? ' sample-evaluation' : '')">
                <div class="tabs-minmax">
                    <img v-if="maxTabs" src="@/assets/images/minimize-2.svg" @click="changeMaxTabs()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip003 != null ? this.languageStrings.ui.tooltip003 : 'Minimalizovat'" />
                    <img v-else src="@/assets/images/maximize-2.svg" @click="changeMaxTabs()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip002 != null ? this.languageStrings.ui.tooltip002 : 'Maximalizovat'" />
                </div>

                <SamplesComponent :samples="samples" @selected-samples="setSelectedSamples" @show-attachments="showSampleAttachments" :selected-items="selectedSamples" @double-click="sampleDoubleClick" :language-strings="languageStrings" :role="role" />
            </div>


            <div v-if="selectedTab === 2" :class="'analyzes-table' + (this.showSampleEvaluation ? ' sample-evaluation' : '')">
                <div class="tabs-minmax">
                    <img v-if="maxTabs" src="@/assets/images/minimize-2.svg" @click="changeMaxTabs()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip003 != null ? this.languageStrings.ui.tooltip003 : 'Minimalizovat'" />
                    <img v-else src="@/assets/images/maximize-2.svg" @click="changeMaxTabs()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip002 != null ? this.languageStrings.ui.tooltip002 : 'Maximalizovat'" />
                </div>

                <AnalyzesComponent :analyzes="analyzesDataToDisplay" @selected-analyze="setSelectedAnalyze" @show-attachments="showAnalyzeAttachments" :selected-item="selectedAnalyze" :role="role" />
            </div>


            <div v-if="selectedTab === 3" class="senzors-panel">
                <SensorsComponent v-if="selectedFolder > 0 || selectedMachines.length > 0"  :force-rerender="rerenderSensors" :grafana-link="grafanaLink" :selected-machines="selectedMachines" :machines="machines" 
                    @sensors-select-machine="sensorsSelectMachine" @selected-sensor="setSelectedSensor" :language-strings="languageStrings" :tab-state="sensorsTabState" @save-tab-state="saveSensorsTabState"
                    :role="role" @move-sensor="moveSensor"></SensorsComponent>
            </div>

            <div v-if="selectedTab === 4" class="events-table">
                <div class="tabs-minmax">
                    <img v-if="maxTabs" src="@/assets/images/minimize-2.svg" @click="changeMaxTabs()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip003 != null ? this.languageStrings.ui.tooltip003 : 'Minimalizovat'" />
                    <img v-else src="@/assets/images/maximize-2.svg" @click="changeMaxTabs()" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip002 != null ? this.languageStrings.ui.tooltip002 : 'Maximalizovat'" />
                </div>

                <EventsComponent :events="sortedEvents" @selected-event="setSelectedEvent" :selected-item="selectedEvent" :language-strings="languageStrings" />
            </div>
            

            <div v-if="selectedTab === 5" class="machine-details">
                <MachineDetailsComponent :machine="machine" :language-strings="languageStrings" :role="role" :is-linde="isSelectedFolderLinde" :local-languages="localLanguageFiles" />
            </div>
        </div>
        <!-- Tabs - samples, analyzes, sensors, events, machine info -->


        <!-- Graphs pane -->
        <div v-if="selectedTab === 2" :class="'analyzes-graphs' + (lockAnalyzesGraphs ? ' locked' : '')">
            <div class="pane-button-strip">
                <button class="button-strip chevrons-left-right" @click="changeLockAnalyzesGraphs()">
                    <img v-if="lockAnalyzesGraphs" src="@/assets/images/chevrons-right.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip005 != null ? this.languageStrings.ui.tooltip005 : 'Skrýt panel'" />
                    <img v-else src="@/assets/images/chevrons-left.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip004 != null ? this.languageStrings.ui.tooltip004 : 'Zobrazit panel'" />
                </button>

                <button class="button-strip chevrons-up-down" @click="changeLockAnalyzesGraphs()">
                    <img v-if="lockAnalyzesGraphs" src="@/assets/images/chevrons-down.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip005 != null ? this.languageStrings.ui.tooltip005 : 'Skrýt panel'" />
                    <img v-else src="@/assets/images/chevrons-up.svg" 
                        :title="this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.tooltip004 != null ? this.languageStrings.ui.tooltip004 : 'Zobrazit panel'" />
                </button>
            </div>

            <div class="analyzes-charts">
                <div v-if="selectedAnalyze !== -1 && this.diagnosis.length > 0" class="analyze-diagnosis">{{ this.diagnosis }}</div>
                <div v-if="selectedAnalyze !== -1 && this.actions != null && this.actions.length > 0" class="analyze-diagnosis">{{ this.actions }}</div>

                <div class="analyze-photo" v-for="(ps, i) in photoSrc" :key="i"><img :src="ps" /></div>

                <div v-for="(gd, i) in graphData" :key="i" class="analyzes-line-chart">
                    <AnalyzesLineChartAsync :chart-data="gd" />
                </div>
            </div>
        </div>
        <!-- Graphs pane -->

    </div>


    <!-- Download attachments form -->
    <div v-if="showDownloadForm" class="attachments-download-form" @click="closeDownloadForm()">
        <div id="downloadForm" @keydown="handleDownloadFormKeyDown" tabindex="-1" @click.stop="stopClickPropagation()">
            <span class="title">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.attachment004 != null ? languageStrings.ui.attachment004 : 'Přílohy' }}</span>
            <img src="@/assets/images/x.svg" @click="closeDownloadForm()" />
            <button class="button-small" @click="downloadAllAttachments">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.attachment002 != null ? languageStrings.ui.attachment002 : 'Stáhnout vše' }}</button>
            <div>
                <Table v-if="showDownloadForm && attachmentsToDownload.length > 0" :headers="attachmentsToDownloadHeaders" :rows="attachmentsToDownloadRows" @selected-items="downloadAttachments" />
            </div>
        </div>
    </div>
    <!-- Download attachments form -->


    <!-- Edit form - folders, machines, samples -->
    <EditForm v-if="showEditForm" :title="editFormTitle" @form-closed="editFormClosed" :edit-data="editFormData" :user-language="userLanguage"
        :saveButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form001 != null ? languageStrings.ui.form001 : 'Uložit'"
        :cancelButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form002 != null ? languageStrings.ui.form002 : 'Zrušit'"
        :yesButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form003 != null ? languageStrings.ui.form003 : 'Ano'"
        :noButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form004 != null ? languageStrings.ui.form004 : 'Ne'"
        :saveText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form005 != null ? languageStrings.ui.form005 : 'Uložit změny?'"
    />
    <!-- Edit form - folders, machines, samples -->


    <!-- Sensor form -->
    <SensorForm v-if="showSensorForm" @form-closed="sensorFormClosed" :role="role" :user-language="userLanguage" :language-strings="languageStrings" :sensors-list="sensorsList" :edit-sensor="selectedSensor" />
    <!-- Sensor form -->


    <!-- Message box - Question -->
    <MessageBoxQuestion v-if="showMessageBoxQuestion" :text="messageBoxQuestionText" @form-closed="messageBoxQuestionClosed" :type="messageBoxType" :data="messageBoxData"
        :yesButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form003 != null ? languageStrings.ui.form003 : 'Ano'"
        :noButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form004 != null ? languageStrings.ui.form004 : 'Ne'"
    />
    <!-- Message box - Question -->


    <!-- Message box -->
    <MessageBox v-if="showMessageBox" :text="messageBoxText" @form-closed="messageBoxClosed" :show-buttons="showMessageBoxButtons" />
    <!-- Message box -->
</template>